import React, { useContext, useEffect, useMemo, useState, useCallback, useRef, useLayoutEffect } from "react";
import axios from "axios";
import { toast } from "@/components/ui/sonner";
import { Inbox, ShoppingCart, Factory, ShieldCheck, Truck, CheckCircle2, LayoutDashboard, ChevronDown, ChevronRight, AlertTriangle, Pencil, Check, Zap, Eye, ArrowLeft, ArrowRight, ShieldAlert, ShieldOff, MessageSquare, ClipboardCheck, CheckCheck, Hourglass, Activity, IndianRupee, EyeOff, ArrowDownToLine, Warehouse, Package, Lock, Clock, CalendarClock, Flag, Filter, Search, Upload, Download } from "lucide-react";
import { AuthContext } from "@/App";
import {
  ResponsiveContainer, ComposedChart, Bar, Line, Area, XAxis, YAxis,
  CartesianGrid, Tooltip, ReferenceLine, Cell,
} from "recharts";
import {
  ECO, TYPE_COLORS, inr, inrDigits, inrShort, inrLakh, fmtDate,
  paymentHint, receivedRatio, FY_DEFAULT,
} from "@/lib/ecotile";
import { searchCatalogue, ECO_CAT_TABS, UNIT_OPTIONS, unitFactor, unitTypeFor } from "@/lib/ecotileCatalogue";
import EcotileStores from "@/components/EcotileStores";
import { placePipeThumb, afterNextFrame } from "@/lib/pipeThumb";
import EcotileFgSourcing from "@/components/EcotileFgSourcing";
import { ProductGlyph, ecotileKind } from "@/components/EcotileGlyph";
import { EcotileImportModal, exportOrders } from "@/components/EcotileImport";
import { ecoBus, EcoMentionInput, EcoSearchBox } from "@/components/EcotileHeaderTools";
import WaffleGlyph from "@/components/icons/WaffleGlyph";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// tone -> tailwind text colour (light/dark safe via explicit values)
const TONE_TEXT = {
  green: "text-emerald-600 dark:text-emerald-400",
  orange: "text-orange-600 dark:text-orange-400",
  red: "text-red-600 dark:text-red-400",
  amber: "text-yellow-700 dark:text-yellow-400",
  slate: "text-slate-500 dark:text-slate-400",
};

// ── little pieces ───────────────────────────────────────────────────────────
function TypeChip({ t }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-[hsl(var(--muted-foreground))] whitespace-nowrap">
      <i className="w-[7px] h-[7px] rounded-[2px] shrink-0" style={{ background: TYPE_COLORS[t] || "#888" }} />{t}
    </span>
  );
}

function PayIndicator({ o }) {
  const hint = paymentHint(o);
  const ratio = receivedRatio(o);
  const barColor = hint.tone === "green" ? ECO.green : hint.tone === "red" ? ECO.red
    : hint.tone === "amber" ? ECO.yellowDeep : "#9aa3a1";
  return (
    <div className="min-w-0">
      <div className={`text-[11.5px] font-semibold truncate ${TONE_TEXT[hint.tone] || ""}`} title={hint.text}>{hint.text}</div>
      <div className="mt-1 h-[3px] rounded-full bg-[hsl(var(--muted))]/70 overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${Math.round(ratio * 100)}%`, background: barColor }} />
      </div>
    </div>
  );
}

// ── filter chips ─────────────────────────────────────────────────────────────
const CHIPS = [
  ["all", "All"], ["pend", "Payment pending"], ["overdue", "Overdue", "red"],
  ["nodl", "No deadline", "amber"], ["upcoming", "Upcoming due"],
  ["fu", "Follow-up ⚑"], ["dispp", "Dispatch pending"],
];
function matchFilter(o, f) {
  if (f === "all") return true;
  if (f === "pend") return o.payment_bucket !== "paid";
  if (f === "overdue") return o.payment_bucket === "overdue";
  if (f === "nodl") return o.payment_bucket === "nodl";
  if (f === "upcoming") return o.payment_bucket === "upcoming";
  if (f === "fu") return !!o.followup_needed;
  if (f === "dispp") return o.dispatch_status === "Pending";
  return true;
}

// ── row detail ───────────────────────────────────────────────────────────────
function DetailRow({ o, colSpan, canRecord, canManage, onChanged, onEdit }) {
  const [amt, setAmt] = useState("");
  const [ddl, setDdl] = useState(o.deadline || "");
  const [busy, setBusy] = useState(false);

  const recordPayment = async () => {
    const v = parseFloat(amt);
    if (!v || v <= 0) { toast.error("Enter a payment amount"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/ecotile/orders/${o.id}/payments`, { amount: v, note: "Recorded in RockLink" });
      toast.success(`Recorded ${inr(v)} for ${o.organisation_name}`);
      setAmt(""); onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not record payment"); }
    finally { setBusy(false); }
  };
  const setDeadline = async () => {
    if (!ddl) { toast.error("Pick a deadline date"); return; }
    setBusy(true);
    try {
      await axios.patch(`${API}/ecotile/orders/${o.id}`, { deadline: ddl });
      toast.success("Deadline set"); onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not set deadline"); }
    finally { setBusy(false); }
  };

  const Row = ({ k, v }) => (
    <div className="flex justify-between gap-4 py-0.5 text-[12px]"><span className="text-[hsl(var(--muted-foreground))]">{k}</span><span className="font-medium text-right">{v}</span></div>
  );
  return (
    <tr>
      <td colSpan={colSpan} className="bg-[hsl(var(--muted))]/25 px-5 py-3.5 border-b border-[hsl(var(--border))]">
        <div className="flex flex-wrap gap-x-10 gap-y-4">
          <div className="min-w-[240px]">
            <div className="text-[10px] font-bold uppercase tracking-wider text-[hsl(var(--muted-foreground))] mb-1.5">{o.organisation_name}</div>
            <Row k="Order" v={<TypeChip t={o.order_type} />} />
            <Row k="PO number" v={o.po_number || "—"} />
            {o.parent_order_id && <Row k="Parent order" v={<span className="text-[hsl(var(--muted-foreground))]">⤳ rolls up</span>} />}
            {o.needs_parent_link && <Row k="Parent" v={<span className="text-yellow-700 dark:text-yellow-400">not linked — pick in app</span>} />}
            <Row k="Booked → invoiced" v={`${inr(o.po_value_basic)} → ${o.invoiced_value_basic ? inr(o.invoiced_value_basic) : "not invoiced"}`} />
            <Row k="Time to dispatch" v={o.time_to_dispatch_days != null ? `${o.time_to_dispatch_days} days` : "—"} />
            {o.comments && <Row k="Note" v={<span className="max-w-[280px] inline-block">{o.comments}</span>} />}
            {o.followup_note && <Row k="Follow-up" v={o.followup_note} />}
          </div>
          <div className="min-w-[240px]">
            <div className="text-[10px] font-bold uppercase tracking-wider text-[hsl(var(--muted-foreground))] mb-1.5">Payment (all-inclusive)</div>
            <Row k="Total value" v={inr(o.total_value_incl)} />
            <Row k="Received so far" v={inr(o.received_so_far)} />
            <Row k="Pending" v={<span className={o.amount_pending > 0 ? "text-red-600 dark:text-red-400" : "text-emerald-600 dark:text-emerald-400"}>{inr(o.amount_pending)}</span>} />
            <Row k="Deadline" v={o.deadline ? fmtDate(o.deadline) : "none set"} />
            <Row k="Status" v={o.payment_status || "—"} />
          </div>
          <div className="min-w-[240px] flex-1">
            <div className="text-[10px] font-bold uppercase tracking-wider text-[hsl(var(--muted-foreground))] mb-1.5">Actions</div>
            {canManage && <button onClick={() => onEdit(o)} className="mb-2 mr-2 h-8 px-3 rounded-full text-[12px] font-medium border border-[hsl(var(--border))] hover:border-[#E6B800]">Edit order</button>}
            {canRecord && o.payment_bucket !== "paid" ? (
              <div className="flex items-center gap-2 mb-2">
                <input value={amt} onChange={(e) => setAmt(e.target.value)} inputMode="decimal" placeholder="amount ₹"
                       className="w-28 h-8 px-2 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--background))] text-sm" />
                <button onClick={recordPayment} disabled={busy}
                        className="h-8 px-3 rounded-full text-[12px] font-bold disabled:opacity-50"
                        style={{ background: "#FFCC00", color: "#2b2500" }}>Record payment</button>
              </div>
            ) : (
              <div className="text-[11px] text-[hsl(var(--muted-foreground))] mb-2">
                {o.payment_bucket === "paid" ? "Payment complete — nothing pending." : "You don’t have permission to record payments."}
              </div>
            )}
            {canManage && o.payment_bucket !== "paid" && (
              <div className="flex items-center gap-2">
                <input type="date" value={ddl ? String(ddl).slice(0, 10) : ""} onChange={(e) => setDdl(e.target.value)}
                       className="h-8 px-2 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--background))] text-sm" />
                <button onClick={setDeadline} disabled={busy}
                        className="h-8 px-3 rounded-full text-[12px] font-medium border border-[hsl(var(--border))] hover:border-[#E6B800] disabled:opacity-50">Set deadline</button>
              </div>
            )}
          </div>
        </div>
      </td>
    </tr>
  );
}

// ── register table ────────────────────────────────────────────────────────────
function Register({ orders, filter, setFilter, q, setQ, openId, setOpenId, canRecord, canManage, reload, onEdit }) {
  const rows = useMemo(() => {
    let r = orders.filter((o) => matchFilter(o, filter));
    if (q) {
      const ql = q.toLowerCase();
      r = r.filter((o) => (`${o.organisation_name} ${o.po_number} ${o.invoice_number}`).toLowerCase().includes(ql));
    }
    return r;
  }, [orders, filter, q]);

  return (
    <>
      <div className="flex items-center gap-2 flex-wrap py-3">
        {CHIPS.map(([k, label, tone]) => {
          const active = filter === k;
          const base = "px-3 py-1.5 rounded-full text-[11.5px] font-bold border transition-colors whitespace-nowrap";
          const cls = active
            ? tone === "red" ? "rl-pressed bg-red-500 border-red-500 text-white"
              : tone === "amber" ? "rl-pressed bg-[#ffdd55] border-[#d4af00] text-[#4d3c00]"
              : "rl-pressed bg-[#FFCC00] border-[#E6B800] text-[#2b2500]"
            : "rl-pill bg-[hsl(var(--card))] border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]";
          return <button key={k} onClick={() => setFilter(k)} className={`${base} ${cls}`}>{label}</button>;
        })}
        <EcoSearchBox value={q} onChange={setQ} placeholder="Search client / PO / invoice…" className="ml-auto" />
      </div>

      <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] rl-tile rl-glass-edge overflow-hidden">
        <table className="w-full table-fixed border-collapse">
          <colgroup>
            <col style={{ width: "34px" }} /><col /><col style={{ width: "108px" }} /><col style={{ width: "150px" }} />
            <col style={{ width: "104px" }} /><col style={{ width: "82px" }} /><col style={{ width: "92px" }} />
            <col style={{ width: "150px" }} /><col style={{ width: "82px" }} /><col style={{ width: "104px" }} />
            <col style={{ width: "200px" }} />
          </colgroup>
          <thead className="rl-thead">
            <tr className="text-[10px] uppercase tracking-wide text-[hsl(var(--muted-foreground))] border-b border-[hsl(var(--border))]">
              <th className="text-right font-semibold px-2 py-2">#</th>
              <th className="text-left font-semibold px-2 py-2">Organisation</th>
              <th className="text-left font-semibold px-2 py-2">Type</th>
              <th className="text-left font-semibold px-2 py-2">PO number</th>
              <th className="text-right font-semibold px-2 py-2">PO value</th>
              <th className="text-right font-semibold px-2 py-2">PO date</th>
              <th className="text-left font-semibold px-2 py-2">Dispatch</th>
              <th className="text-left font-semibold px-2 py-2">Invoice #</th>
              <th className="text-right font-semibold px-2 py-2">Inv date</th>
              <th className="text-right font-semibold px-2 py-2">Invoiced</th>
              <th className="text-left font-semibold px-2 py-2" style={{ background: "rgba(255,204,0,.06)" }}>Payment</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={11} className="px-4 py-14 text-center">
                <div className="text-[15px] font-semibold mb-1">
                  {filter === "overdue" ? "No overdue payments 🎉" : q ? `No orders match “${q}”` : "Nothing here yet"}
                </div>
                <div className="text-[hsl(var(--muted-foreground))] text-sm">
                  {filter === "overdue" ? "Every pending payment is within its deadline." : "Orders in this view will appear here."}
                </div>
                <button onClick={() => { setFilter("all"); setQ(""); }} className="mt-3 px-3 py-1.5 rounded-full text-[12px] font-medium border border-[hsl(var(--border))]">Show all orders</button>
              </td></tr>
            )}
            {rows.map((o) => {
              const open = openId === o.id;
              return (
                <React.Fragment key={o.id}>
                  <tr onClick={() => setOpenId(open ? null : o.id)}
                      className={`cursor-pointer border-b border-[hsl(var(--border))] hover:bg-[#FFCC00]/[0.06] ${open ? "bg-[#FFCC00]/[0.08]" : ""}`}>
                    <td className="text-right px-2 py-1.5 text-[11px] text-[hsl(var(--muted-foreground))] tabular-nums">{o.seq_no}</td>
                    <td className="px-2 py-1.5 truncate font-medium" title={o.organisation_name}>{o.organisation_name}</td>
                    <td className="px-2 py-1.5"><TypeChip t={o.order_type} /></td>
                    <td className="px-2 py-1.5 truncate text-[11.5px] text-[hsl(var(--muted-foreground))]" title={o.po_number}>
                      {o.po_number || "—"}
                      {o.needs_parent_link && <span className="ml-1 text-[10px] text-yellow-700 dark:text-yellow-400" title="Likely belongs to a parent order">⤳?</span>}
                    </td>
                    <td className="text-right px-2 py-1.5 tabular-nums">{o.po_value_basic == null ? "•••" : inrDigits(o.po_value_basic)}</td>
                    <td className="text-right px-2 py-1.5 text-[11.5px] text-[hsl(var(--muted-foreground))] tabular-nums">{fmtDate(o.po_received_date)}</td>
                    <td className="px-2 py-1.5 text-[11.5px]">{o.dispatch_status === "Complete"
                      ? <span className="text-emerald-600 dark:text-emerald-400 font-semibold">Complete</span>
                      : <span className="text-orange-600 dark:text-orange-400 font-semibold">Pending</span>}</td>
                    <td className="px-2 py-1.5 truncate text-[11px] text-[hsl(var(--muted-foreground))]" title={o.invoice_number}>{o.invoice_number || "—"}</td>
                    <td className="text-right px-2 py-1.5 text-[11.5px] text-[hsl(var(--muted-foreground))] tabular-nums">{fmtDate(o.invoice_date)}</td>
                    <td className="text-right px-2 py-1.5 tabular-nums">{o.invoiced_value_basic == null ? "•••" : (o.invoiced_value_basic ? inrDigits(o.invoiced_value_basic) : "—")}</td>
                    <td className="px-2 py-1.5" style={{ background: "rgba(255,204,0,.05)" }}><PayIndicator o={o} /></td>
                  </tr>
                  {open && <DetailRow o={o} colSpan={11} canRecord={canRecord} canManage={canManage} onChanged={reload} onEdit={onEdit} />}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="text-[11px] text-[hsl(var(--muted-foreground))] mt-2 px-1">
        Showing {rows.length} of {orders.length} orders. Booked / Invoiced are basic (pre-GST); payment figures are all-inclusive. Click a row for full detail.
      </div>
    </>
  );
}

// ── payments board ───────────────────────────────────────────────────────────
// Header disc + carved total strip mirror the metallic stat cards: the bucket's
// hue lives in an rl-ico-well disc instead of a flat bar, rows are rl-well so
// they lift under the cursor, and the summed pending moves out of the subtitle
// caption into its own carved footer.
const BUCKET_ICON = { red: AlertTriangle, amber: Clock, slate: CalendarClock };
function Bucket({ title, tone, rows, note }) {
  const barBg = tone === "red" ? "#ef4444" : tone === "amber" ? "#f59e0b" : "#64748b";
  const Ic = BUCKET_ICON[tone] || CalendarClock;
  const total = rows.reduce((s, o) => s + (Number(o.amount_pending) || 0), 0);
  return (
    <div className="flex-1 min-w-[300px] rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] rl-tile overflow-hidden">
      <div className="flex items-center gap-3 px-4 py-3 border-b border-[hsl(var(--border))]">
        <span className="w-9 h-9 rounded-[10px] rl-ico-well flex items-center justify-center shrink-0" style={{ background: `${barBg}1f`, color: barBg }}>
          <Ic className="w-[18px] h-[18px]" />
        </span>
        <div className="min-w-0">
          <div className="font-bold text-[13px] truncate">{title}</div>
          <div className="text-[11px] text-[hsl(var(--muted-foreground))]">{note}</div>
        </div>
        <div className="ml-auto font-mono-data text-[19px] font-extrabold leading-none tabular-nums" style={{ color: barBg }}>{rows.length}</div>
      </div>
      <div className="max-h-[520px] overflow-auto py-1">
        {rows.length === 0 && <div className="px-4 py-8 text-center text-[hsl(var(--muted-foreground))] text-sm">Nothing here — all clear.</div>}
        {rows.map((o) => (
          <div key={o.id} className="mx-1.5 my-0.5 px-2.5 py-2 rounded-[10px] rl-well">
            <div className="flex items-center justify-between gap-2">
              <div className="font-semibold text-[12.5px] truncate" title={o.organisation_name}>{o.organisation_name}</div>
              <div className="text-[12.5px] font-bold tabular-nums whitespace-nowrap font-mono-data">{inr(o.amount_pending)}</div>
            </div>
            <div className="flex items-center justify-between gap-2 mt-0.5">
              <div className="text-[11px] text-[hsl(var(--muted-foreground))] truncate">{o.po_number} · <TypeChip t={o.order_type} /></div>
              <div className="text-[11px] font-semibold whitespace-nowrap" style={{ color: barBg }}>
                {tone === "red" ? `overdue ${o.days_overdue}d` : tone === "amber" ? "no deadline" : `due ${fmtDate(o.deadline)}`}
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between px-4 py-2 border-t border-[hsl(var(--border))] rl-carved bg-[hsl(var(--muted))]/50">
        <span className="text-[11px] text-[hsl(var(--muted-foreground))]">Pending in this bucket</span>
        <b className="text-[12.5px] font-mono-data tabular-nums">{inr(total)}</b>
      </div>
    </div>
  );
}
function PaymentsBoard({ orders }) {
  const overdue = orders.filter((o) => o.payment_bucket === "overdue").sort((a, b) => (b.days_overdue || 0) - (a.days_overdue || 0));
  const nodl = orders.filter((o) => o.payment_bucket === "nodl").sort((a, b) => (b.amount_pending || 0) - (a.amount_pending || 0));
  const up = orders.filter((o) => o.payment_bucket === "upcoming").sort((a, b) => (a.days_overdue || 0) - (b.days_overdue || 0));
  return (
    <div className="flex flex-wrap gap-4 pt-4">
      <Bucket title="Overdue — past deadline" tone="red" rows={overdue} note="chase now" />
      <Bucket title="No deadline set" tone="amber" rows={nodl} note="agree a date" />
      <Bucket title="Upcoming — due later" tone="slate" rows={up} note="on track" />
    </div>
  );
}

// ── targets ──────────────────────────────────────────────────────────────────
function TargetsView({ targets, dark }) {
  if (!targets || !targets.months?.length) return <div className="pt-8 text-center text-[hsl(var(--muted-foreground))]">No targets loaded for this FY.</div>;
  const cInv = dark ? "#B78E10" : "#A67F00";
  const cBook = dark ? "#22A377" : "#1D8F6B";
  const cTgt = dark ? "#c9cfcd" : "#3F4444";
  const monthly = targets.months.map((m, i) => ({
    m, target: targets.monthly_target_lakh[i], invoiced: targets.monthly_invoiced_lakh[i], booked: targets.monthly_booked_lakh[i],
  }));
  const cum = targets.months.map((m, i) => ({
    m, ctarget: targets.cumulative_target_lakh[i],
    cinvoiced: i < (targets.cumulative_invoiced_lakh || []).length ? targets.cumulative_invoiced_lakh[i] : null,
  }));
  const fyTarget = targets.cumulative_target_lakh[targets.cumulative_target_lakh.length - 1] || 0;
  const invSoFar = (targets.cumulative_invoiced_lakh || []).slice(-1)[0] || 0;
  const pct = fyTarget ? (invSoFar / fyTarget * 100) : 0;
  return (
    <div className="pt-4 space-y-4 max-w-[1180px]">
      <div className="rounded-xl px-5 py-4 flex flex-wrap gap-8 items-center" style={{ background: ECO.grey }}>
        <div><div className="text-[20px] font-extrabold" style={{ color: "#FFCC00" }}>{inrLakh(fyTarget * 100000)}</div><div className="text-[11px] text-white/60">FY 2026-27 target</div></div>
        <div><div className="text-[20px] font-extrabold text-white">{inrLakh(invSoFar * 100000)}</div><div className="text-[11px] text-white/60">Invoiced so far · month 4 of 12</div></div>
        <div><div className="text-[20px] font-extrabold" style={{ color: "#ffe27a" }}>{pct.toFixed(1)}%</div><div className="text-[11px] text-white/60">of annual target</div></div>
      </div>

      <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4">
        <div className="flex items-baseline gap-3 mb-2"><h3 className="font-bold text-[13.5px]">Monthly — target vs invoiced vs booked</h3><span className="text-[11px] text-[hsl(var(--muted-foreground))]">₹ lakh</span></div>
        <ResponsiveContainer width="100%" height={250}>
          <ComposedChart data={monthly} margin={{ top: 8, right: 12, bottom: 4, left: -8 }}>
            <CartesianGrid strokeDasharray="2 2" stroke="hsl(var(--border))" vertical={false} />
            <XAxis dataKey="m" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
            <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12, border: "1px solid hsl(var(--border))", background: "hsl(var(--card))" }} formatter={(v, n) => [v ? v.toFixed(2) + " L" : "—", n]} />
            <Bar dataKey="invoiced" name="Invoiced" fill={cInv} radius={[3, 3, 0, 0]} maxBarSize={18} />
            <Bar dataKey="booked" name="Booked" fill="none" stroke={cBook} strokeWidth={1.6} radius={[3, 3, 0, 0]} maxBarSize={18} />
            <Line dataKey="target" name="Target" stroke={cTgt} strokeWidth={2} strokeDasharray="5 3" dot={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4">
        <div className="flex items-baseline gap-3 mb-2"><h3 className="font-bold text-[13.5px]">Cumulative — target vs invoiced</h3><span className="text-[11px] text-[hsl(var(--muted-foreground))]">₹ lakh · invoiced stops at today</span></div>
        <ResponsiveContainer width="100%" height={260}>
          <ComposedChart data={cum} margin={{ top: 8, right: 12, bottom: 4, left: -8 }}>
            <CartesianGrid strokeDasharray="2 2" stroke="hsl(var(--border))" vertical={false} />
            <XAxis dataKey="m" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
            <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12, border: "1px solid hsl(var(--border))", background: "hsl(var(--card))" }} formatter={(v, n) => [v != null ? v.toFixed(2) + " L" : "—", n]} />
            <ReferenceLine x="Jul" stroke="hsl(var(--border))" strokeDasharray="2 3" label={{ value: "today", fontSize: 10, fill: "hsl(var(--muted-foreground))" }} />
            <Area dataKey="cinvoiced" name="Cumulative invoiced" stroke={cInv} strokeWidth={2.5} fill={cInv} fillOpacity={0.1} connectNulls={false} />
            <Line dataKey="ctarget" name="Cumulative target" stroke={cTgt} strokeWidth={2} dot={false} />
          </ComposedChart>
        </ResponsiveContainer>
        <div className="text-[10.5px] text-[hsl(var(--muted-foreground))] mt-2">Monthly booked sums slightly below total booked because 3 orders carry pre-April PO dates. Targets are the FY sheet’s monthly value targets; cumulative = running sum.</div>
      </div>
    </div>
  );
}

// ── lean Ecotile stage constants ─────────────────────────────────────────────
const PIPE_STAGES = ["PO Received", "Procurement", "Manufacturing", "Quality Control", "Dispatch"];
const FULFIL = ["FG stock", "Existing RM", "Order new material"];

// ── chevron pipeline nav + StagePage-style stage view (borrows the ERP DNA) ──
// Stage palette = the metallic ERP's own vivid stage hues (unify, don't invent —
// the earlier muted variants read dull next to RockLink main).
const STAGE_META = {
  "PO Received":     { short: "PO Received",   color: "#3b82f6", Icon: Inbox },
  "Procurement":     { short: "Procurement",   color: "#f97316", Icon: ShoppingCart },
  "Manufacturing":   { short: "Manufacturing", color: "#a855f7", Icon: Factory },
  "Quality Control": { short: "QC",            color: "#14b8a6", Icon: ShieldCheck },
  "Dispatch":        { short: "Dispatch",      color: "#f59e0b", Icon: Truck },
  "Completed":       { short: "Completed",     color: "#22c55e", Icon: CheckCircle2 },
};
const NAV_STAGES = [...PIPE_STAGES, "Completed"];

function EcotilePipelineNav({ counts, active, onSelect }) {
  const thumbRef = useRef(null);
  const tabRefs = useRef(new Map());
  const mounted = useRef(false);
  // Thumb placement: GPU pieces, see lib/pipeThumb.js.
  const placeFor = useCallback((key, instant) => {
    const idx = NAV_STAGES.indexOf(key);
    placePipeThumb(thumbRef.current, tabRefs.current.get(key), {
      color: STAGE_META[key]?.color,
      isFirst: idx === 0,
      isLast: idx === NAV_STAGES.length - 1,
      instant,
    });
  }, []);
  const place = useCallback((instant) => placeFor(active, instant), [active, placeFor]);
  // A click starts the glide and whitens the label NOW; the view switch
  // follows one frame later, so the glide never waits for it.
  const goTo = (key) => {
    if (key === active) { onSelect(key); return; }
    placeFor(key, false);
    tabRefs.current.forEach((el, k) => el.classList.toggle("active", k === key));
    afterNextFrame(() => onSelect(key));
  };
  useLayoutEffect(() => { const inst = !mounted.current; mounted.current = true; place(inst); }, [place]);
  useEffect(() => {
    const r = () => place(true);
    window.addEventListener("resize", r);
    return () => window.removeEventListener("resize", r);
  }, [place]);
  return (
    <div className="pipeline-nav">
      <div ref={thumbRef} className="pipe-thumb" aria-hidden="true"><i className="l" /><i className="m" /><i className="r" /></div>
      {NAV_STAGES.map((s, i) => {
        const M = STAGE_META[s];
        const isActive = s === active;
        const Icon = M.Icon;
        return (
          <React.Fragment key={s}>
            {i > 0 && (
              <span className={`pipe-sep ${isActive || NAV_STAGES[i - 1] === active ? "pipe-sep-hide" : ""}`} aria-hidden="true">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M9 5l7 7-7 7" /></svg>
              </span>
            )}
            <div className="chevron-wrapper">
              <button ref={(el) => { if (el) tabRefs.current.set(s, el); else tabRefs.current.delete(s); }}
                onClick={() => goTo(s)} className={`pipe-tab ${isActive ? "active" : ""}`} style={{ "--stage": M.color }}>
                <Icon className="w-4 h-4" style={!isActive ? { color: M.color } : undefined} />
                <span>{M.short}</span>
                {counts[s] > 0 && <span className="tab-count">{counts[s]}</span>}
              </button>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ── stage page: RockLink-skinned rows (glass, hover action cluster, expedite/blocked) ──
const MFG_STATUSES = ["Queued", "In production", "Complete"];
const DISPATCH_STATUSES = ["Pending", "Pickup awaited", "Dispatched"];
const ACCEPTANCE_CHECKS = [
  ["price", "Price & order values are correct"],
  ["payment_terms", "Payment terms agreed & recorded"],
  ["address", "Delivery / billing address confirmed"],
  ["advance", "Necessary advance payment taken"],
  ["incoterms", "Incoterms / freight terms are clear"],
];

function DRow({ k, v }) {
  return (
    <div className="flex justify-between gap-4 py-0.5 text-[12px]">
      <span className="text-[hsl(var(--muted-foreground))] whitespace-nowrap">{k}</span>
      <span className="font-medium text-right">{v}</span>
    </div>
  );
}

// days the order has sat in its current stage (from the last stage_history entry)
function stageAgeDays(o) {
  const hist = o.stage_history || [];
  const at = hist.length ? hist[hist.length - 1].at : (o.updated_at || o.created_at);
  if (!at) return null;
  const d = Math.floor((Date.now() - new Date(at).getTime()) / 86400000);
  return d >= 0 && Number.isFinite(d) ? d : null;
}

// one action-cluster button: rl-well disc + icon-zoom on hover + tiny caption,
// hover-revealed via the row's group/row (unless persistent).
function RowAction({ icon: Icon, label, onClick, hoverColor, captionColor, reveal = true, persist = false, badge, testid, disabled }) {
  const revealCls = reveal && !persist
    ? "opacity-0 pointer-events-none group-hover/row:opacity-100 group-hover/row:pointer-events-auto group-focus-within/row:opacity-100 group-focus-within/row:pointer-events-auto"
    : "";
  return (
    <div className={`flex flex-col items-center transition-opacity duration-150 ${revealCls}`}>
      <button type="button" onClick={onClick} disabled={disabled} aria-label={label} title={label} data-testid={testid}
        className={`relative inline-flex items-center justify-center w-7 h-7 rounded-full rl-well transition-colors ${hoverColor || "text-[hsl(var(--muted-foreground))]/70 hover:text-[hsl(var(--foreground))]"} active:scale-95 disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500/40`}>
        <Icon className="w-4 h-4" />
        {badge != null && badge > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[14px] h-[14px] px-1 rounded-full bg-[#FFCC00] text-[#2b2500] text-[9px] font-bold flex items-center justify-center leading-none">{badge}</span>
        )}
      </button>
      <span className={`text-[8.5px] uppercase tracking-wide font-semibold leading-none mt-0.5 ${captionColor || "text-[hsl(var(--muted-foreground))]/70"}`} aria-hidden="true">{label}</span>
    </div>
  );
}

// ── stage-cockpit widgets (mock v2: metallic AttentionChip / velocity skins) ──
// One filter vocabulary with the register: chips are click-to-filter tiles that
// press IN when active (rl-pressed + colour-tinted inset), exactly like the ERP.
function AttnTile({ Icon, label, count, color, active, onClick, dim, testid }) {
  const clickable = !!onClick;
  return (
    <button type="button" onClick={onClick} disabled={!clickable} data-testid={testid}
      title={clickable ? (active ? `Clear ${label} filter` : `Filter to ${count} ${label}`) : undefined}
      className={`group inline-flex items-center gap-2.5 py-1.5 pl-1.5 pr-3 rounded-[10px] border shrink-0 rl-tile ${active ? "rl-pressed" : ""} ${clickable ? "" : "cursor-default"}`}
      style={{
        background: active ? `${color}12` : "hsl(var(--card))",
        borderColor: active ? `${color}59` : "hsl(var(--border))",
        boxShadow: active ? `inset 0 2px 5px ${color}30` : undefined,
        opacity: dim ? 0.6 : 1,
      }}>
      <span className="flex items-center justify-center w-7 h-7 rounded-full rl-ico-well shrink-0" style={{ background: `${color}1f` }}>
        <Icon className="w-[15px] h-[15px]" style={{ color }} aria-hidden="true" />
      </span>
      <span className="flex flex-col items-start leading-tight">
        <span className="text-[10px] font-bold uppercase tracking-[0.08em] whitespace-nowrap" style={{ color: active ? color : "hsl(var(--muted-foreground))" }}>{label}</span>
        <span className="font-mono-data font-extrabold tabular-nums text-[17px]" style={{ color, letterSpacing: "-0.02em" }}>{count}</span>
      </span>
      {clickable && (
        <Filter className={`w-3 h-3 shrink-0 ml-0.5 transition-opacity duration-150 ${active ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`} style={{ color, fill: active ? color : "transparent" }} aria-hidden="true" />
      )}
    </button>
  );
}

// shared row grid — the columnar anatomy every stage row + the header share
const ROW_GRID = "minmax(215px,275px) minmax(185px,1fr) 100px 175px minmax(245px,310px) 310px";

// one product line: catalogue glyph + carved code chip + qty (Part#-chip grammar)
function ProdLine({ li, exp }) {
  return (
    <div className="flex items-center gap-2 text-[12px] my-0.5 min-w-0">
      <ProductGlyph code={li.item_code || ""} hex={li.hex} tex={li.tex} size={20} />
      <span className={`font-mono-data text-[12px] font-semibold px-1.5 py-0.5 rounded-md whitespace-nowrap ${exp ? "rl-expedite-chip" : "rl-carved bg-[hsl(var(--muted))]/50"}`}>{li.item_code || (li.description || "").slice(0, 18) || "—"}</span>
      {li.qty ? <span className={`text-[11.5px] whitespace-nowrap truncate ${exp ? "rl-expedite-comment" : "text-[hsl(var(--muted-foreground))]"}`}>{li.qty} {li.unit || ""}</span> : null}
    </div>
  );
}

// ── order area stats (expanded panel): floor area from TILE lines, ramp length
// from RAMP lines, shown in the unit system the order was entered in.
const SQFT_PER_M2 = 10.7639, FT_PER_M = 3.28084;
function orderAreaStats(items) {
  let floorM2 = 0, rampM = 0, imp = 0, met = 0;
  (items || []).forEach((li) => {
    const kind = ecotileKind(li.item_code || "");
    const ut = li.ut || unitTypeFor(li.unit);
    if (ut === "area" || ut === "length") { (/f(oo|ee)?t/i.test(li.unit || "") ? imp++ : met++); }
    const baseQty = (Number(li.qty) || 0) * (unitFactor(ut, li.unit) || 1);
    if (kind === "tile" && ut === "area") floorM2 += baseQty;
    if (kind === "ramp" && ut === "length") rampM += baseQty;
  });
  return { floorM2, rampM, imperial: imp > met };
}
const fmtQ = (n) => (Math.round(n * 10) / 10).toLocaleString("en-IN");

// ── notes lightbox (mirrors the metallic StagePage: notes live in a modal,
// not buried in the expanded row) ──
function EcoNotesLightbox({ o, canManage, onNote, onClose }) {
  if (!o) return null;
  return (
    <div className="fixed inset-0 z-[70] flex items-start justify-center p-4 overflow-auto" style={{ background: "rgba(28,24,16,.34)" }} onClick={onClose}
      onKeyDown={(e) => { if (e.key === "Escape") onClose(); }}>
      <div className="rl-frost rl-hero-glass w-full max-w-[440px] rounded-2xl border shadow-2xl mt-20" onClick={(e) => e.stopPropagation()} data-testid="eco-notes-lightbox">
        <div className="flex items-start justify-between px-5 py-3.5 border-b border-[hsl(var(--border))]">
          <div>
            <div className="font-bold text-[15px] flex items-center gap-2"><MessageSquare className="w-4 h-4 text-[#c8992a]" /> Notes</div>
            <div className="text-[12px] text-[hsl(var(--muted-foreground))] mt-0.5">{o.organisation_name}{o.po_number ? ` · ${o.po_number}` : ""}</div>
          </div>
          <button onClick={onClose} aria-label="Close" className="w-7 h-7 rounded-full rl-well flex items-center justify-center text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] text-[16px] leading-none">×</button>
        </div>
        <div className="px-5 py-4">
          <NoteList o={o} canManage={canManage} onNote={onNote} />
        </div>
      </div>
    </div>
  );
}

// ── payment lightbox — the payment column's click target (record + deadline) ──
function EcoPaymentLightbox({ o, canRecord, canManage, onRecord, onPatch, onClose }) {
  const [amt, setAmt] = useState("");
  const [ddl, setDdl] = useState(o?.deadline ? String(o.deadline).slice(0, 10) : "");
  if (!o) return null;
  const rec = () => { const t = amt; setAmt(""); onRecord(t); };
  return (
    <div className="fixed inset-0 z-[70] flex items-start justify-center p-4 overflow-auto" style={{ background: "rgba(28,24,16,.34)" }} onClick={onClose}
      onKeyDown={(e) => { if (e.key === "Escape") onClose(); }}>
      <div className="rl-frost rl-hero-glass w-full max-w-[420px] rounded-2xl border shadow-2xl mt-20" onClick={(e) => e.stopPropagation()} data-testid="eco-pay-lightbox">
        <div className="flex items-start justify-between px-5 py-3.5 border-b border-[hsl(var(--border))]">
          <div>
            <div className="font-bold text-[15px] flex items-center gap-2"><IndianRupee className="w-4 h-4 text-[#B78E10]" /> Payment</div>
            <div className="text-[12px] text-[hsl(var(--muted-foreground))] mt-0.5">{o.organisation_name}{o.po_number ? ` · ${o.po_number}` : ""}</div>
          </div>
          <button onClick={onClose} aria-label="Close" className="w-7 h-7 rounded-full rl-well flex items-center justify-center text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] text-[16px] leading-none">×</button>
        </div>
        <div className="px-5 py-4">
          <div className="mb-3"><PayIndicator o={o} /></div>
          <DRow k="Total (all-incl.)" v={o.total_value_incl == null ? "•••" : inr(o.total_value_incl)} />
          <DRow k="Received" v={o.received_so_far == null ? "•••" : inr(o.received_so_far)} />
          <DRow k="Pending" v={<span className={o.amount_pending > 0 ? "text-red-600 dark:text-red-400" : "text-emerald-600 dark:text-emerald-400"}>{o.amount_pending == null ? "•••" : inr(o.amount_pending)}</span>} />
          <DRow k="Deadline" v={o.deadline ? fmtDate(o.deadline) : "none set"} />
          <DRow k="Status" v={o.payment_status || "—"} />
          {canRecord && o.payment_bucket !== "paid" && (
            <div className="flex items-center gap-2 mt-3">
              <input value={amt} onChange={(e) => setAmt(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); rec(); } }} inputMode="decimal" placeholder="amount ₹  (Enter)" autoFocus
                className="flex-1 h-9 px-2.5 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--background))] text-sm outline-none focus:border-[#E6B800]" />
              <button onClick={rec} className="h-9 px-4 rounded-full rl-lit-btn text-[12px] font-bold" style={{ background: "#FFCC00", color: "#2b2500" }}>Record</button>
            </div>
          )}
          {canManage && (
            <div className="flex items-center gap-2 mt-2.5">
              <input type="date" value={ddl} onChange={(e) => setDdl(e.target.value)}
                className="flex-1 h-9 px-2.5 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--background))] text-sm outline-none focus:border-[#E6B800]" />
              <button onClick={() => ddl && onPatch({ deadline: ddl })} className="h-9 px-3.5 rounded-full rl-well text-[12px] font-semibold border border-[hsl(var(--border))] hover:border-[#E6B800]">Set deadline</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// contract-review acceptance popup — gates PO Received → Procurement.
function AcceptancePopup({ o, onClose, onConfirm }) {
  const [ck, setCk] = useState({});
  const [busy, setBusy] = useState(false);
  const all = ACCEPTANCE_CHECKS.every(([k]) => ck[k]);
  const confirm = async () => { if (!all) return; setBusy(true); try { await onConfirm(); } finally { setBusy(false); } };
  return (
    <div className="fixed inset-0 z-[70] flex items-start justify-center p-4 overflow-auto" style={{ background: "rgba(28,24,16,.34)" }} onClick={onClose}
      onKeyDown={(e) => { if (e.key === "Escape") onClose(); }}>
      <div className="rl-frost rl-hero-glass w-full max-w-[460px] rounded-2xl border shadow-2xl mt-16" onClick={(e) => e.stopPropagation()} data-testid="eco-accept-popup">
        <div className="px-5 py-3.5 border-b border-[hsl(var(--border))]">
          <div className="font-bold text-[15px]">Contract review — accept order</div>
          <div className="text-[12px] text-[hsl(var(--muted-foreground))] mt-0.5">{o.organisation_name}{o.po_number ? ` · ${o.po_number}` : ""}</div>
        </div>
        <div className="px-5 py-4 space-y-1">
          <div className="text-[12px] text-[hsl(var(--muted-foreground))] mb-1.5">Confirm each item before moving to Procurement:</div>
          {ACCEPTANCE_CHECKS.map(([k, label]) => (
            <label key={k} className="flex items-center gap-2.5 py-1.5 px-2 rounded-lg cursor-pointer hover:bg-[hsl(var(--muted))]/40" data-testid={`eco-accept-${k}`}>
              <span className={`w-5 h-5 rounded-md flex items-center justify-center shrink-0 border transition-colors ${ck[k] ? "bg-[#188653] border-[#188653]" : "border-[hsl(var(--border))]"}`}>
                {ck[k] && <Check className="w-3.5 h-3.5 text-white" />}
              </span>
              <input type="checkbox" className="sr-only" checked={!!ck[k]} onChange={(e) => setCk((s) => ({ ...s, [k]: e.target.checked }))} />
              <span className="text-[13px]">{label}</span>
            </label>
          ))}
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-3.5 border-t border-[hsl(var(--border))]">
          <button onClick={onClose} className="h-9 px-4 rounded-full text-[13px] font-medium border border-[hsl(var(--border))]">Cancel</button>
          <button onClick={confirm} disabled={!all || busy} title={all ? "" : "Tick every item to accept"} data-testid="eco-accept-confirm"
            className={`h-9 px-5 rounded-full text-[13px] font-bold disabled:opacity-40 transition-colors ${all ? "rl-lit-btn" : ""}`}
            style={{ background: all ? "#188653" : "#9aa3a1", color: "#fff" }}>
            {busy ? "…" : "Accept & advance →"}
          </button>
        </div>
      </div>
    </div>
  );
}

function NoteList({ o, canManage, onNote }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const notes = o.notes || [];
  const add = async () => {
    const t = text.trim(); if (!t) return;
    setBusy(true); try { await onNote(t); setText(""); } finally { setBusy(false); }
  };
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-wider text-[hsl(var(--muted-foreground))] mb-1.5">Notes</div>
      {notes.length === 0 ? (
        <div className="text-[12px] text-[hsl(var(--muted-foreground))] mb-2">No notes yet.</div>
      ) : (
        <div className="space-y-1.5 mb-2 max-h-32 overflow-auto pr-1">
          {notes.slice().reverse().map((n, i) => (
            <div key={i} className="text-[12px] rounded-md bg-[hsl(var(--muted))]/40 px-2 py-1.5">
              <div className="whitespace-pre-wrap break-words">{n.text}</div>
              <div className="text-[10px] text-[hsl(var(--muted-foreground))] mt-0.5">{n.by || "—"}{n.at ? ` · ${fmtDate(n.at)}` : ""}</div>
            </div>
          ))}
        </div>
      )}
      {canManage && (
        <div className="flex items-center gap-2">
          <EcoMentionInput value={text} onChange={setText} onEnter={add} placeholder="Add a note…  (@ to tag · Enter to save)"
            className="w-full h-8 px-2 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--background))] text-[12px] outline-none focus:border-[#E6B800]" />
          <button onClick={add} disabled={busy} className="h-8 px-3 rounded-full rl-well text-[12px] font-medium border border-[hsl(var(--border))] hover:border-[#E6B800] disabled:opacity-50">Add</button>
        </div>
      )}
    </div>
  );
}

function StageRow({ o, stage, stageIdx, isFirstStage, canManage, canRecord, busy, expanded, onToggle, onAdvance, onAccept, onBack, onExpedite, onPatch, onNote, onNotes, onPay, onRecord, onEdit, onSource }) {
  const needFul = stage === "Procurement" && !o.fulfilment;
  const fgSkip = stage === "Procurement" && o.fulfilment === "FG stock";
  const blocked = !!o.blocked;
  const isExpedite = !!o.expedite && !blocked;
  const M = STAGE_META[stage];
  const [blking, setBlking] = useState(false);
  const [blkText, setBlkText] = useState("");
  const notesN = (o.notes || []).length;
  const days = stageAgeDays(o);
  const ageCol = days == null ? null : days >= 10 ? "#e0685e" : days >= 5 ? "#e8a13a" : null;

  const startProblem = () => { if (!expanded) onToggle(); setBlking(true); };
  const doBlock = () => { onPatch({ blocked: true, block_reason: blkText.trim() || "Flagged" }); setBlking(false); setBlkText(""); };
  const commitOnEnter = (e) => { if (e.key === "Enter") e.currentTarget.blur(); };

  // ── stage-specific control column (mock v2) ──
  // Procurement: ONE segmented fulfilment control; the supplier code is a quiet
  // dashed chip; FG stock reveals Source-from-FG + the reservation pill.
  const segBtn = (f, Icon, label) => (
    <button key={f} type="button" disabled={!canManage || blocked}
      onClick={() => onPatch({ fulfilment: f, procurement_status: f === "FG stock" ? "Not needed" : f === "Existing RM" ? "Received" : "Pending" })}
      className={`flex items-center gap-1 h-[26px] px-2.5 rounded-full text-[10.5px] font-semibold whitespace-nowrap transition-colors ${o.fulfilment === f ? "bg-[#FFCC00] text-[#2b2500] font-bold rl-thumb" : "text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"}`}>
      <Icon className="w-[11px] h-[11px]" /> {label}
    </button>
  );
  let Ctl = null;
  if (stage === "PO Received") Ctl = (
    <button type="button" onClick={canManage ? onAccept : undefined} disabled={!canManage || blocked}
      className="inline-flex items-center gap-1.5 h-[26px] px-2.5 rounded-full text-[11px] font-semibold border border-[rgba(200,150,40,0.42)] bg-[rgba(232,184,74,0.08)] text-[#8a6a1c] dark:text-[#e9c877] hover:bg-[rgba(232,184,74,0.16)] transition-colors disabled:opacity-50"
      title="Opens the contract-review checklist">
      <ClipboardCheck className="w-3 h-3" /> Contract review pending
    </button>
  );
  else if (stage === "Procurement") Ctl = (
    <div className="flex flex-col items-start gap-1.5 min-w-0">
      <div className={`inline-flex items-center p-[3px] rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/50 rl-carved ${blocked ? "opacity-55 pointer-events-none" : ""}`} data-testid={`eco-fulfil-${o.seq_no}`}>
        {segBtn("FG stock", Warehouse, "FG stock")}
        {segBtn("Existing RM", Package, "Existing RM")}
        {segBtn("Order new material", ShoppingCart, "Order new")}
      </div>
      {blocked ? (
        <span className="text-[10.5px] text-[#a15048] dark:text-[#e8938a]">sourcing paused while blocked</span>
      ) : o.fulfilment && o.fulfilment !== "FG stock" ? (
        <label className="inline-flex items-center gap-1.5 h-6 px-2 rounded-[7px] border border-dashed border-[hsl(var(--border))] bg-[hsl(var(--muted))]/35">
          <span className="text-[9.5px] uppercase tracking-wide text-[hsl(var(--muted-foreground))]">supplier</span>
          <input value={o.rm_supplier_code || ""} disabled={!canManage} placeholder="code" onKeyDown={commitOnEnter}
            onChange={(e) => onPatch({ rm_supplier_code: e.target.value })}
            className="w-24 bg-transparent outline-none font-mono-data text-[11px]" />
        </label>
      ) : o.fulfilment === "FG stock" ? (
        <div className="flex items-center gap-1.5 flex-wrap">
          {canManage && !o.fg_reservation?.issued && (
            <button type="button" onClick={() => onSource && onSource(o)} data-testid={`eco-source-${o.seq_no}`}
              className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-md border border-blue-500/30 bg-blue-500/8 text-blue-700 dark:text-blue-300 hover:bg-blue-500/15 hover:border-blue-500/50 transition-colors">
              <ArrowDownToLine className="w-3 h-3" /> Source from FG
            </button>
          )}
          {o.fg_reservation && o.fg_reservation.tiles_reserved != null && o.fg_reservation.tiles_reserved > 0 && (
            <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-full rl-pill whitespace-nowrap border ${o.fg_reservation.shortfall > 0 ? "text-orange-600 dark:text-orange-400 border-orange-500/40 bg-orange-500/5" : "text-emerald-600 dark:text-emerald-400 border-emerald-500/40 bg-emerald-500/5"}`}
              title={o.fg_reservation.issued ? "Issued — shipped out of FG stock" : "Reserved against FG stock"}>
              {o.fg_reservation.issued ? "✓ issued" : "◧ reserved"} {o.fg_reservation.tiles_reserved} tiles{o.fg_reservation.location ? ` · ${o.fg_reservation.location}` : ""}{o.fg_reservation.shortfall > 0 ? ` · short ${o.fg_reservation.shortfall}` : ""}
            </span>
          )}
        </div>
      ) : (
        <span className="text-[10.5px] text-[hsl(var(--muted-foreground))]">choose how this order is fulfilled</span>
      )}
    </div>
  );
  else if (stage === "Manufacturing") Ctl = (
    <div className="flex items-center gap-1.5 flex-wrap">
      <select value={o.mfg_status || "Queued"} disabled={!canManage} onChange={(e) => onPatch({ mfg_status: e.target.value })}
        className="h-[26px] pl-2.5 pr-1.5 rounded-full border text-[11px] font-bold rl-pill cursor-pointer outline-none"
        style={{ color: M.color, borderColor: `${M.color}59`, background: `${M.color}12` }}>
        {MFG_STATUSES.map((s) => <option key={s}>{s}</option>)}
      </select>
      <label className="inline-flex items-center gap-1.5 h-6 px-2 rounded-[7px] border border-dashed border-[hsl(var(--border))] bg-[hsl(var(--muted))]/35">
        <span className="text-[9.5px] uppercase tracking-wide text-[hsl(var(--muted-foreground))]">batch</span>
        <input value={o.mfg_batch || ""} disabled={!canManage} placeholder="#" onKeyDown={commitOnEnter}
          onChange={(e) => onPatch({ mfg_batch: e.target.value })}
          className="w-16 bg-transparent outline-none font-mono-data text-[11px]" />
      </label>
    </div>
  );
  else if (stage === "Quality Control") Ctl = (
    <button type="button" disabled={!canManage} onClick={() => onPatch({ qc_passed: !o.qc_passed })}
      className="inline-flex items-center gap-1.5 h-[26px] px-2.5 rounded-full text-[11px] font-bold border rl-pill transition-colors"
      style={o.qc_passed
        ? { color: M.color, borderColor: `${M.color}59`, background: `${M.color}12` }
        : { color: "hsl(var(--muted-foreground))", borderColor: "hsl(var(--border))", background: "hsl(var(--card))" }}>
      {o.qc_passed ? <Check className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
      {o.qc_passed ? "Shade / QC passed" : "Shade check pending"}
    </button>
  );
  else if (stage === "Dispatch") Ctl = (
    <div className="flex items-center gap-1.5 flex-wrap">
      <select value={o.dispatch_mode || "Pending"} disabled={!canManage} onChange={(e) => onPatch({ dispatch_mode: e.target.value })}
        className="h-[26px] pl-2.5 pr-1.5 rounded-full border text-[11px] font-bold rl-pill cursor-pointer outline-none"
        style={{ color: M.color, borderColor: `${M.color}59`, background: `${M.color}12` }}>
        {DISPATCH_STATUSES.map((s) => <option key={s}>{s}</option>)}
      </select>
      <label className="inline-flex items-center gap-1.5 h-6 px-2 rounded-[7px] border border-dashed border-[hsl(var(--border))] bg-[hsl(var(--muted))]/35">
        <span className="text-[9.5px] uppercase tracking-wide text-[hsl(var(--muted-foreground))]">inv #</span>
        <input value={o.invoice_number || ""} disabled={!canManage} placeholder="number" onKeyDown={commitOnEnter}
          onChange={(e) => onPatch({ invoice_number: e.target.value })}
          className="w-28 bg-transparent outline-none font-mono-data text-[11px]" />
      </label>
      <label className="inline-flex items-center gap-1.5 h-6 px-2 rounded-[7px] border border-dashed border-[hsl(var(--border))] bg-[hsl(var(--muted))]/35">
        <span className="text-[9.5px] uppercase tracking-wide text-[hsl(var(--muted-foreground))]">inv ₹</span>
        <input defaultValue={o.invoiced_value_basic ?? ""} disabled={!canManage} placeholder="basic" inputMode="decimal" onKeyDown={commitOnEnter}
          onBlur={(e) => { const v = parseFloat(e.target.value); if (!Number.isNaN(v) && v !== o.invoiced_value_basic) onPatch({ invoiced_value_basic: v }); }}
          className="w-20 bg-transparent outline-none font-mono-data text-[11px] tabular-nums" />
      </label>
    </div>
  );

  // advance/accept (rl-lit-btn, stage-tinted) + greyed-with-reason caption
  const advDisabled = busy || needFul || blocked;
  const lockCap = blocked ? "resolve the block first" : needFul ? "choose a source first" : null;
  const advAction = stage === "PO Received" ? onAccept : onAdvance;
  const advInner = stage === "PO Received"
    ? <><ClipboardCheck className="w-3 h-3" />Accept</>
    : fgSkip ? <>Dispatch<ArrowRight className="w-3 h-3" /></>
    : stage === "Dispatch" ? <><CheckCheck className="w-3.5 h-3.5" />Complete</>
    : <>Advance<ArrowRight className="w-3 h-3" /></>;

  const lineItems = o.line_items || [];

  return (
    <div className={`group/row item-row relative border-b border-[hsl(var(--border))]/50 hover:bg-[hsl(var(--muted))]/20 transition-colors ${isExpedite ? "rl-expedite-row" : ""} ${blocked ? "blocked-row" : ""}`} data-testid={`eco-row-${o.seq_no}`} data-eco-id={o.id}>
      <div className="grid items-center" style={{ gridTemplateColumns: ROW_GRID }}>

        {/* ORDER — the eye IS the expand affordance: pressable disc before the
            name (recessed when open, lifts on row hover to say "expandable"). */}
        <div role="button" tabIndex={0} onClick={onToggle}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onToggle(); } }}
          className="group/oc px-3.5 py-3 min-w-0 cursor-pointer select-none" data-testid={`eco-expand-${o.seq_no}`}>
          {isExpedite && (
            <span className="rl-gold-badge inline-flex items-center gap-0.5 text-[9px] font-extrabold uppercase tracking-[0.08em] px-1.5 py-[2.5px] rounded-md ml-9 mb-1"><Zap className="w-2.5 h-2.5" />Expedite</span>
          )}
          <div className="flex items-center gap-2 min-w-0">
            <button type="button" aria-label={expanded ? "Collapse details" : "Expand details"} aria-expanded={expanded}
              onClick={(e) => { e.stopPropagation(); onToggle(); }} data-testid={`eco-details-${o.seq_no}`}
              className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center border transition-all ${expanded
                ? "rl-carved bg-[hsl(var(--muted))]/70 border-[hsl(var(--border))] text-[hsl(var(--foreground))]"
                : "border-transparent text-[hsl(var(--muted-foreground))]/45 group-hover/oc:bg-[hsl(var(--card))] group-hover/oc:border-[hsl(var(--border))] group-hover/oc:text-[hsl(var(--foreground))] group-hover/oc:shadow-sm"}`}>
              <Eye className="w-[15px] h-[15px]" />
            </button>
            <span className={`text-[13px] font-semibold truncate ${isExpedite ? "rl-expedite-text" : blocked ? "rl-blocked-text" : ""}`} title={o.organisation_name}>{o.organisation_name}</span>
            {blocked && <span className="rl-blocked-badge inline-flex items-center text-[9px] font-extrabold uppercase tracking-[0.08em] px-1.5 py-[2.5px] rounded-md shrink-0">Blocked</span>}
          </div>
          <div className={`pl-9 text-[11px] truncate flex items-center gap-1.5 ${blocked ? "rl-blocked-meta" : "text-[hsl(var(--muted-foreground))]"}`}>
            <TypeChip t={o.order_type} /> · <span className="font-mono-data opacity-75">#{o.seq_no}</span>
          </div>
          {blocked && o.block_reason && (
            <div className="pl-9 mt-1 text-[11px] rl-blocked-reason flex items-start gap-1">
              <ShieldAlert className="w-3 h-3 shrink-0 mt-[1px]" /><span>{o.block_reason}</span>
            </div>
          )}
        </div>

        {/* PO · PRODUCTS — PO number first (clear, not faint), every line below */}
        <div className="px-3 py-3 min-w-0">
          <div className={`font-mono-data text-[12px] font-semibold mb-1 truncate ${blocked ? "rl-blocked-meta" : isExpedite ? "rl-expedite-meta" : "text-[hsl(var(--foreground))]"}`}>
            <span className="text-[9px] uppercase tracking-wide mr-1 font-sans font-bold text-[hsl(var(--muted-foreground))]">PO</span>{o.po_number || "no PO"}
          </div>
          {lineItems.length > 0 ? (
            lineItems.map((li, i) => <ProdLine key={i} li={li} exp={isExpedite} />)
          ) : (
            <span className="text-[11.5px] text-[hsl(var(--muted-foreground))]">{[o.product_type, o.thickness, o.colour].filter(Boolean).join(" · ") || "—"}{o.area_sqft ? ` · ${o.area_sqft}` : ""}</span>
          )}
        </div>

        {/* VALUE */}
        <div className="px-3 py-3 text-right">
          <span className={`font-mono-data text-[13px] font-semibold tabular-nums ${isExpedite ? "rl-expedite-text" : ""}`}>{o.po_value_basic == null ? "•••" : inr(o.po_value_basic)}</span>
        </div>

        {/* PAYMENT — click anywhere in the cell (or the ghost chip) → payment lightbox */}
        <div role="button" tabIndex={0} onClick={() => onPay(o)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onPay(o); } }}
          className="px-3 py-3 min-w-0 cursor-pointer" title="Open payment panel" data-testid={`eco-paycell-${o.seq_no}`}>
          <PayIndicator o={o} />
          {o.followup_needed && (
            <span className="inline-flex items-center gap-1 mt-1 px-1.5 py-[1px] rounded-md text-[9px] font-extrabold uppercase tracking-wide text-[#8A6FB8] border border-[rgba(138,111,184,0.4)] bg-[rgba(138,111,184,0.07)]">
              <Flag className="w-[9px] h-[9px]" /> follow-up
            </span>
          )}
          {canRecord && o.payment_bucket !== "paid" && (
            <span className="block w-fit mt-1 h-5 px-2 leading-[18px] rounded-full border border-dashed border-[rgba(200,150,40,0.5)] text-[10px] font-bold text-[#8a6a00] dark:text-[#e9c877] bg-[rgba(255,204,0,0.06)] opacity-0 group-hover/row:opacity-100 transition-opacity" data-testid={`eco-quickrec-${o.seq_no}`}>
              + record payment
            </span>
          )}
        </div>

        {/* STAGE CONTROL */}
        <div className="px-3 py-3 min-w-0">{Ctl}</div>

        {/* ACTIONS — persistent core (Back · Problem/Unblock · Advance · Notes-w/-notes), hover extras (Details · Expedite) */}
        <div className="px-4 py-2.5 flex flex-col items-end justify-center gap-1">
          <div className="flex items-start justify-end gap-1.5">
            {/* Notes — hover previews the latest notes (metallic StagePage style), click opens the lightbox */}
            <div className={`relative group/notes flex flex-col items-center transition-opacity duration-150 ${notesN > 0 ? "" : "opacity-0 pointer-events-none group-hover/row:opacity-100 group-hover/row:pointer-events-auto"}`}>
              <button type="button" onClick={() => onNotes(o)} aria-label="Notes" data-testid={`eco-notes-${o.seq_no}`}
                className="relative inline-flex items-center justify-center w-7 h-7 rounded-full rl-well transition-colors text-[hsl(var(--muted-foreground))]/70 hover:text-[hsl(var(--foreground))] active:scale-95">
                <MessageSquare className="w-4 h-4" />
                {notesN > 0 && <span className="absolute -top-1 -right-1 min-w-[14px] h-[14px] px-1 rounded-full bg-[#FFCC00] text-[#2b2500] text-[9px] font-bold flex items-center justify-center leading-none">{notesN}</span>}
              </button>
              <span className="text-[8.5px] uppercase tracking-wide font-semibold leading-none mt-0.5 text-[hsl(var(--muted-foreground))]/70">Notes</span>
              {notesN > 0 && (
                <div className="pointer-events-none absolute bottom-full right-0 mb-2 w-64 rounded-xl rl-frost border border-[hsl(var(--border))] shadow-2xl p-2.5 opacity-0 group-hover/notes:opacity-100 transition-opacity z-40 text-left">
                  {(o.notes || []).slice(-2).reverse().map((n, i) => (
                    <div key={i} className={`text-[11.5px] ${i > 0 ? "mt-2 pt-2 border-t border-[hsl(var(--border))]/60" : ""}`}>
                      <div className="whitespace-pre-wrap break-words line-clamp-3">{n.text}</div>
                      <div className="text-[9.5px] text-[hsl(var(--muted-foreground))] mt-0.5">{n.by || "—"}{n.at ? ` · ${fmtDate(n.at)}` : ""}</div>
                    </div>
                  ))}
                  {notesN > 2 && <div className="text-[9.5px] text-[hsl(var(--muted-foreground))] mt-1.5">+ {notesN - 2} older · click to open</div>}
                </div>
              )}
            </div>
            {!isFirstStage && canManage && (
              <RowAction icon={ArrowLeft} label="Back" persist reveal={false} onClick={() => onBack(o)} hoverColor="text-[hsl(var(--muted-foreground))]/70 hover:text-amber-500" testid={`eco-back-${o.seq_no}`} />
            )}
            {canManage && (blocked
              ? <RowAction icon={ShieldOff} label="Unblock" persist reveal={false} onClick={() => onPatch({ blocked: false, block_reason: "" })} hoverColor="text-red-500 hover:text-red-600" captionColor="text-red-500/80" testid={`eco-unblock-${o.seq_no}`} />
              : <RowAction icon={ShieldAlert} label="Problem" persist reveal={false} onClick={startProblem} hoverColor="text-[hsl(var(--muted-foreground))]/70 hover:text-red-600 dark:hover:text-red-400" testid={`eco-problem-${o.seq_no}`} />)}
            {canManage && stage !== "Completed" && (
              <button type="button" onClick={advAction} disabled={advDisabled} data-testid={`eco-advance-${o.seq_no}`}
                title={lockCap || ""}
                className={`flex items-center gap-1 rounded-full font-bold uppercase tracking-wider px-2.5 py-1 text-[11px] whitespace-nowrap transition-colors ${advDisabled ? "cursor-not-allowed text-[hsl(var(--muted-foreground))]/60 border border-[hsl(var(--border))]" : "text-white rl-lit-btn hover:brightness-110"}`}
                style={advDisabled ? {} : { backgroundColor: M.color }}>
                {busy ? "…" : advDisabled ? <><Lock className="w-3 h-3" />{stage === "PO Received" ? "Accept" : "Advance"}</> : advInner}
              </button>
            )}
          </div>
          {lockCap && (
            <span className="text-[8.5px] font-semibold uppercase tracking-wide text-[#a15048] dark:text-[#e8938a] leading-none">{lockCap}</span>
          )}
          {days != null && stage !== "Completed" && (
            ageCol ? (
              <span className="inline-flex items-center gap-1 rounded-full border rl-pill text-[10px] px-2 py-[2px] tabular-nums leading-none" style={{ color: ageCol, borderColor: `${ageCol}73`, background: `${ageCol}12` }}>
                <Hourglass className="w-[11px] h-[11px]" />{days}d <span className="opacity-80">in stage</span>
              </span>
            ) : (
              <span className="text-[10px] text-[hsl(var(--muted-foreground))]/60 tabular-nums leading-none">{days}d in stage</span>
            )
          )}
        </div>
      </div>

      {expanded && (
        <div className="px-5 pb-4 pt-1 bg-[hsl(var(--muted))]/25 border-t border-[hsl(var(--border))]/60" data-testid={`eco-detail-${o.seq_no}`}>
          <div className="flex flex-wrap gap-x-8 gap-y-4 pt-3">
            <div className="min-w-[230px]">
              <div className="text-[10px] font-bold uppercase tracking-wider text-[hsl(var(--muted-foreground))] mb-1.5">Order stats</div>
              {(o.line_items && o.line_items.length > 0) ? (() => {
                const st = orderAreaStats(o.line_items);
                return (
                  <div className="space-y-0.5">
                    {st.floorM2 > 0 && (
                      <DRow k="Total floor area" v={<span className="font-mono-data font-semibold">{st.imperial ? `${fmtQ(st.floorM2 * SQFT_PER_M2)} sq ft` : `${fmtQ(st.floorM2)} m²`}
                        <span className="font-sans font-normal text-[hsl(var(--muted-foreground))]"> · ≈{Math.round(st.floorM2 * 4).toLocaleString("en-IN")} tiles</span></span>} />
                    )}
                    {st.rampM > 0 && (
                      <DRow k="Total ramp length" v={<span className="font-mono-data font-semibold">{st.imperial ? `${fmtQ(st.rampM * FT_PER_M)} ft` : `${fmtQ(st.rampM)} m`}</span>} />
                    )}
                    <DRow k="Lines" v={o.line_items.length} />
                    <div className="flex justify-between gap-3 text-[12px] pt-1 mt-1 border-t border-[hsl(var(--border))]">
                      <span className="text-[hsl(var(--muted-foreground))]">Booked (basic)</span>
                      <span className="tabular-nums font-semibold">{o.po_value_basic == null ? "•••" : inr(o.po_value_basic)}</span>
                    </div>
                    {o.fg_reservation && o.fg_reservation.tiles_reserved != null && (
                      <DRow k={o.fg_reservation.issued ? "FG issued" : "FG reserved"} v={<span className={o.fg_reservation.shortfall > 0 ? "text-orange-600 dark:text-orange-400" : ""}>{o.fg_reservation.tiles_reserved} tiles @ {o.fg_reservation.location}{o.fg_reservation.shortfall > 0 ? ` · short ${o.fg_reservation.shortfall}` : ""}</span>} />
                    )}
                  </div>
                );
              })() : (
                <div className="space-y-0.5">
                  <DRow k="Product" v={[o.product_type, o.thickness, o.colour].filter(Boolean).join(" · ") || "—"} />
                  <DRow k="Area / qty" v={o.area_sqft || "—"} />
                  <DRow k="Booked → invoiced" v={`${o.po_value_basic == null ? "•••" : inr(o.po_value_basic)} → ${o.invoiced_value_basic ? inr(o.invoiced_value_basic) : "not invoiced"}`} />
                  {o.fulfilment && <DRow k="Fulfilment" v={o.fulfilment === "Order new material" ? "Order new" : o.fulfilment} />}
                  {o.fg_reservation && o.fg_reservation.tiles_reserved != null && (
                    <DRow k={o.fg_reservation.issued ? "FG issued" : "FG reserved"} v={<span className={o.fg_reservation.shortfall > 0 ? "text-orange-600 dark:text-orange-400" : ""}>{o.fg_reservation.tiles_reserved} tiles @ {o.fg_reservation.location}{o.fg_reservation.shortfall > 0 ? ` · short ${o.fg_reservation.shortfall}` : ""}</span>} />
                  )}
                  {o.mfg_batch && <DRow k="Batch" v={o.mfg_batch} />}
                  {o.comments && <DRow k="Comment" v={<span className="max-w-[220px] inline-block">{o.comments}</span>} />}
                </div>
              )}
            </div>
            <div className="min-w-[240px] flex-1">
              {canManage && (
                <div className="mb-3">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-[hsl(var(--muted-foreground))] mb-1.5">Priority</div>
                  <button type="button" onClick={() => onExpedite(o)} data-testid={`eco-expedite-${o.seq_no}`}
                    className={`inline-flex items-center gap-1.5 h-[27px] px-3 rounded-full text-[11.5px] font-semibold border transition-colors ${o.expedite
                      ? "rl-gold-badge"
                      : "rl-well border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:text-amber-600"}`}>
                    <Zap className="w-3 h-3" />
                    {o.expedite ? "Expedited — click to set normal" : "Expedite this order"}
                  </button>
                </div>
              )}
              {blking && (
                <div className="flex items-center gap-2 mt-3">
                  <input value={blkText} onChange={(e) => setBlkText(e.target.value)} autoFocus placeholder="What's blocking this order?  (Enter to block)"
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); doBlock(); } else if (e.key === "Escape") { setBlking(false); setBlkText(""); } }}
                    className="flex-1 h-8 px-2 rounded-md border border-red-500/40 bg-[hsl(var(--background))] text-[12px] outline-none focus:border-red-500" />
                  <button onClick={doBlock} className="h-8 px-3 rounded-full rl-lit-btn text-[12px] font-bold bg-red-500 text-white">Block</button>
                  <button onClick={() => { setBlking(false); setBlkText(""); }} className="h-8 px-2 rounded-full text-[12px] text-[hsl(var(--muted-foreground))]">Cancel</button>
                </div>
              )}
              {blocked && o.block_reason && <div className="text-[12px] text-red-600 dark:text-red-400 mt-3 flex items-start gap-1"><AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" /><span>{o.block_reason}</span></div>}
              {canManage && (
                <div className="mt-3">
                  <button onClick={() => onEdit(o)} className="h-8 px-3 rounded-full rl-well text-[12px] font-medium border border-[hsl(var(--border))] hover:border-[#E6B800] inline-flex items-center gap-1"><Pencil className="w-3 h-3" /> Edit order</button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StageView({ stage, orders, canManage, canRecord, reload, onEdit, focus, onFocusDone }) {
  const [busyId, setBusyId] = useState(null);
  const [openId, setOpenId] = useState(null);
  const rootRef = useRef(null);
  const [acceptFor, setAcceptFor] = useState(null);
  const [sourcingFor, setSourcingFor] = useState(null);
  const [notesForId, setNotesForId] = useState(null);
  const [payForId, setPayForId] = useState(null);
  const [q, setQ] = useState("");
  const [attnFilter, setAttnFilter] = useState(null);   // blocked | overdue | nodl | fu
  const [stageFilter, setStageFilter] = useState(null); // per-stage breakdown key

  // magic-search deep-link: when the palette opens an order in its stage, seed
  // the search + expand + scroll to it (mirrors the metallic search→stage jump).
  useEffect(() => {
    if (!focus || focus.stage !== stage) return;
    if (focus.q != null) { setQ(focus.q); setAttnFilter(null); setStageFilter(null); }
    if (focus.id) {
      setOpenId(focus.id);
      const t = setTimeout(() => {
        const el = rootRef.current?.querySelector(`[data-eco-id="${focus.id}"]`);
        if (el) { el.scrollIntoView({ behavior: "smooth", block: "center" }); el.classList.add("rl-mention-flash"); setTimeout(() => el.classList.remove("rl-mention-flash"), 1500); }
      }, 120);
      return () => clearTimeout(t);
    }
    onFocusDone && onFocusDone();
  }, [focus, stage]); // eslint-disable-line react-hooks/exhaustive-deps

  const M = STAGE_META[stage];
  const stageIdx = NAV_STAGES.indexOf(stage);
  const isFirstStage = stageIdx <= 0;
  const nextStage = NAV_STAGES[stageIdx + 1] || null;

  const stageRows = orders.filter((o) => (o.stage || "PO Received") === stage);

  // attention counts — the REGISTER's vocabulary, promoted to the stage page
  const counts = {
    blocked: stageRows.filter((o) => o.blocked).length,
    overdue: stageRows.filter((o) => o.payment_bucket === "overdue").length,
    nodl: stageRows.filter((o) => o.payment_bucket === "nodl").length,
    fu: stageRows.filter((o) => o.followup_needed).length,
  };
  const hasAttn = counts.blocked > 0 || counts.overdue > 0 || counts.nodl > 0 || counts.fu > 0 || !!attnFilter;

  // stage-specific breakdown chips (Procurement sourcing / status pills elsewhere)
  let stageChips = [];
  if (stage === "Procurement") {
    const n = (f) => stageRows.filter((o) => o.fulfilment === f).length;
    stageChips = [
      { key: "FG stock", label: "FG stock", Icon: Warehouse, color: "#3b82f6", count: n("FG stock") },
      { key: "Existing RM", label: "Existing RM", Icon: Package, color: "#22c55e", count: n("Existing RM") },
      { key: "Order new material", label: "Order new", Icon: ShoppingCart, color: M.color, count: n("Order new material") },
      { key: "__unset", label: "Source not set", Icon: Search, color: "#8a8f98", count: stageRows.filter((o) => !o.fulfilment).length },
    ];
  } else if (stage === "Manufacturing") {
    stageChips = MFG_STATUSES.map((s) => ({ key: s, label: s, Icon: s === "Complete" ? CheckCircle2 : s === "In production" ? Factory : Clock, color: M.color, count: stageRows.filter((o) => (o.mfg_status || "Queued") === s).length }));
  } else if (stage === "Quality Control") {
    stageChips = [
      { key: "__pending", label: "Shade check pending", Icon: Clock, color: "#b45309", count: stageRows.filter((o) => !o.qc_passed).length },
      { key: "__passed", label: "Passed", Icon: CheckCircle2, color: M.color, count: stageRows.filter((o) => !!o.qc_passed).length },
    ];
  } else if (stage === "Dispatch") {
    stageChips = DISPATCH_STATUSES.map((s) => ({ key: s, label: s, Icon: s === "Dispatched" ? CheckCircle2 : s === "Pickup awaited" ? Clock : Truck, color: M.color, count: stageRows.filter((o) => (o.dispatch_mode || "Pending") === s).length }));
  }

  const matchStageFilter = (o) => {
    if (!stageFilter) return true;
    if (stage === "Procurement") return stageFilter === "__unset" ? !o.fulfilment : o.fulfilment === stageFilter;
    if (stage === "Manufacturing") return (o.mfg_status || "Queued") === stageFilter;
    if (stage === "Quality Control") return stageFilter === "__passed" ? !!o.qc_passed : !o.qc_passed;
    if (stage === "Dispatch") return (o.dispatch_mode || "Pending") === stageFilter;
    return true;
  };
  const matchAttn = (o) =>
    !attnFilter ? true
    : attnFilter === "blocked" ? !!o.blocked
    : attnFilter === "overdue" ? o.payment_bucket === "overdue"
    : attnFilter === "nodl" ? o.payment_bucket === "nodl"
    : attnFilter === "fu" ? !!o.followup_needed
    : true;
  const ql = q.trim().toLowerCase();
  const matchQ = (o) => !ql
    || (o.organisation_name || "").toLowerCase().includes(ql)
    || (o.po_number || "").toLowerCase().includes(ql)
    || (o.line_items || []).some((li) => (li.item_code || "").toLowerCase().includes(ql) || (li.description || "").toLowerCase().includes(ql));

  const rows = stageRows.filter(matchAttn).filter(matchStageFilter).filter(matchQ);
  const filtered = rows.length !== stageRows.length;
  const booked = stageRows.reduce((s, o) => s + (Number(o.po_value_basic) || 0), 0);
  const showBooked = stageRows.some((o) => o.po_value_basic != null);

  // velocity — orders that LEFT this stage (a stage_history entry into the next)
  const velocity = useMemo(() => {
    if (!nextStage) return null;
    const now = Date.now();
    let t = 0, w = 0, m = 0;
    orders.forEach((o) => (o.stage_history || []).forEach((h) => {
      if ((h.to || h.to_stage || h.stage) !== nextStage || !h.at) return;
      const d = (now - new Date(h.at).getTime()) / 86400000;
      if (d < 0) return;
      if (d < 1) t++;
      if (d < 7) w++;
      if (d < 31) m++;
    }));
    return { t, w, m };
  }, [orders, nextStage]);
  const veloTitle = stage === "PO Received" ? "Accepted into pipeline"
    : stage === "Procurement" ? "Sourced & advanced"
    : stage === "Manufacturing" ? "Batches into QC"
    : stage === "Quality Control" ? "Cleared into Dispatch"
    : stage === "Dispatch" ? "Orders completed" : null;

  const advance = async (o) => {
    setBusyId(o.id);
    try { await axios.post(`${API}/ecotile/orders/${o.id}/advance`, {}); toast.success(`${o.organisation_name} → advanced`); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not advance"); }
    finally { setBusyId(null); }
  };
  const back = async (o) => {
    const prev = NAV_STAGES[stageIdx - 1]; if (!prev) return;
    setBusyId(o.id);
    try { await axios.patch(`${API}/ecotile/orders/${o.id}`, { stage: prev }); toast.success(`${o.organisation_name} → ${prev}`); reload(); }
    catch (e) { toast.error("Could not move back"); }
    finally { setBusyId(null); }
  };
  const expedite = async (o) => {
    setBusyId(o.id);
    try { await axios.patch(`${API}/ecotile/orders/${o.id}`, { expedite: !o.expedite }); reload(); }
    catch (e) { toast.error("Could not update"); }
    finally { setBusyId(null); }
  };
  const patch = async (o, body) => {
    setBusyId(o.id);
    try { await axios.patch(`${API}/ecotile/orders/${o.id}`, body); reload(); }
    catch (e) { toast.error("Could not save"); }
    finally { setBusyId(null); }
  };
  const note = async (o, text) => {
    try { await axios.post(`${API}/ecotile/orders/${o.id}/note`, { text }); toast.success("Note added"); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not add note"); }
  };
  const record = async (o, amt) => {
    const v = parseFloat(amt); if (!v || v <= 0) { toast.error("Enter a payment amount"); return; }
    try { await axios.post(`${API}/ecotile/orders/${o.id}/payments`, { amount: v, note: "Recorded in RockLink" }); toast.success(`Recorded ${inr(v)}`); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not record payment"); }
  };
  const confirmAccept = async () => { const o = acceptFor; setAcceptFor(null); await advance(o); };

  const thCls = "px-3 py-2.5 text-[11px] uppercase tracking-wider font-semibold text-[hsl(var(--muted-foreground))]";
  const stageColLabel = stage === "PO Received" ? "Contract review"
    : stage === "Procurement" ? "Sourcing · Fulfilment"
    : stage === "Manufacturing" ? "Mfg status · Batch"
    : stage === "Quality Control" ? "QC status"
    : stage === "Dispatch" ? "Dispatch status" : "";

  return (
    <div ref={rootRef} className="mt-4 rounded-xl rl-glass-edge overflow-hidden bg-[hsl(var(--card))] border border-[hsl(var(--border))]">
      {/* stage accent rule (fills in on stage switch, same as the ERP) */}
      <div className="h-1 rl-stage-accent" style={{ background: `linear-gradient(90deg, ${M.color}, ${M.color}59)`, transformOrigin: "left" }} />

      {/* head — tick + title + counts + ₹ booked + carved search */}
      <div className="flex items-center gap-3 px-5 pt-4 pb-3 flex-wrap">
        <span className="w-1 h-[22px] rounded-[2px] shrink-0" style={{ background: M.color }} />
        <h2 className="text-[20px] font-bold tracking-[-0.01em] leading-none">{stage}</h2>
        <span className="text-[13px] text-[hsl(var(--muted-foreground))]">
          {filtered ? `${rows.length} of ${stageRows.length}` : stageRows.length} order{stageRows.length === 1 ? "" : "s"}{filtered ? " · filtered" : ""}
        </span>
        {showBooked && <span className="font-mono-data text-[13px] text-[hsl(var(--muted-foreground))]/85 tabular-nums">· {inr(booked)} booked</span>}
        <label className="ml-auto flex items-center gap-2 w-[250px] h-9 px-3 rounded-[10px] border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/40 rl-search">
          <Search className="w-3.5 h-3.5 text-[hsl(var(--muted-foreground))] shrink-0" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search client / PO / product…"
            className="flex-1 min-w-0 bg-transparent outline-none text-[12.5px]" data-testid="eco-stage-search" />
          {q && <button type="button" onClick={() => setQ("")} className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] leading-none">×</button>}
        </label>
      </div>

      {/* attention + stage breakdown strips, velocity tile right */}
      {stageRows.length > 0 && (hasAttn || stageChips.length > 0 || (velocity && veloTitle)) && (
        <div className="flex items-stretch gap-4 px-5 pb-3.5">
          <div className="flex-1 min-w-0 space-y-2.5">
            {hasAttn && (
              <div className="flex items-center gap-x-2 gap-y-2 flex-wrap">
                <span className="hidden md:inline-block w-[76px] shrink-0 text-[10px] font-bold uppercase tracking-[0.16em] text-[hsl(var(--muted-foreground))]/80 select-none">Attention</span>
                {[
                  ["blocked", "Blocked", ShieldAlert, "#ef4444", counts.blocked],
                  ["overdue", "Payment overdue", Clock, "#ea580c", counts.overdue],
                  ["nodl", "No deadline", CalendarClock, "#b45309", counts.nodl],
                  ["fu", "Follow-up", Flag, "#8A6FB8", counts.fu],
                ].filter(([k, , , , c]) => c > 0 || attnFilter === k).map(([k, label, Icon, color, c]) => (
                  <AttnTile key={k} Icon={Icon} label={label} count={c} color={color} active={attnFilter === k}
                    onClick={() => setAttnFilter(attnFilter === k ? null : k)} testid={`eco-attn-${k}`} />
                ))}
                {(attnFilter || stageFilter) && (
                  <button type="button" onClick={() => { setAttnFilter(null); setStageFilter(null); }}
                    className="flex items-center gap-1 text-[11px] font-semibold text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors px-2 py-1 rounded-md hover:bg-[hsl(var(--muted))]" data-testid="eco-clear-filter">
                    × Clear filter
                  </button>
                )}
              </div>
            )}
            {hasAttn && stageChips.length > 0 && <div className="hidden md:block border-t border-[hsl(var(--border))]/50" />}
            {stageChips.length > 0 && (
              <div className="flex items-center gap-x-2 gap-y-2 flex-wrap">
                <span className="hidden md:inline-block w-[76px] shrink-0 text-[10px] font-bold uppercase tracking-[0.16em] text-[hsl(var(--muted-foreground))]/80 select-none">{stage === "Procurement" ? "Sourcing" : "Status"}</span>
                {stageChips.map((c) => (
                  <AttnTile key={c.key} Icon={c.Icon} label={c.label} count={c.count} color={c.color} dim={c.count === 0 && stageFilter !== c.key} active={stageFilter === c.key}
                    onClick={() => setStageFilter(stageFilter === c.key ? null : c.key)} testid={`eco-chip-${c.key.replace(/\s+/g, "-").toLowerCase()}`} />
                ))}
              </div>
            )}
          </div>
          {velocity && veloTitle && (
            <div className="hidden lg:flex w-[235px] shrink-0 border-l border-[hsl(var(--border))]/40 pl-5 flex-col justify-center gap-1">
              <span className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-[hsl(var(--muted-foreground))]">
                <CheckCircle2 className="w-3.5 h-3.5" style={{ color: M.color }} /> {veloTitle}
              </span>
              <div className="flex gap-4">
                {[[velocity.t, "today"], [velocity.w, "this week"], [velocity.m, "this month"]].map(([n, l]) => (
                  <span key={l} className="whitespace-nowrap"><span className="font-mono-data text-[17px] font-bold tabular-nums">{n}</span> <span className="text-[10px] text-[hsl(var(--muted-foreground))]">{l}</span></span>
                ))}
              </div>
              <span className="text-[10px] text-[hsl(var(--muted-foreground))]">orders that moved past {stage}</span>
            </div>
          )}
        </div>
      )}

      {/* columnar table header (shares ROW_GRID with the rows) */}
      <div className="hidden md:grid items-center rl-thead border-t border-b border-[hsl(var(--border))]" style={{ gridTemplateColumns: ROW_GRID }}>
        <span className={`${thCls} px-4`}>Order</span>
        <span className={thCls}>PO · Products</span>
        <span className={`${thCls} text-right`}>Value</span>
        <span className={thCls}>Payment</span>
        <span className={thCls}>{stageColLabel}</span>
        <span className={`${thCls} px-4 text-right`}>Actions</span>
      </div>

      {rows.length === 0 ? (
        <div className="px-4 py-12 text-center text-[hsl(var(--muted-foreground))]">
          <div className="text-[14px] font-medium text-[hsl(var(--foreground))] mb-1">{stageRows.length === 0 ? `No orders at ${stage}` : "No orders match the current filters"}</div>
          {stageRows.length === 0 ? "Orders reach this stage as they advance through the pipeline." : "Clear the filters above to see the whole stage."}
        </div>
      ) : rows.map((o) => (
        <StageRow key={o.id} o={o} stage={stage} stageIdx={stageIdx} isFirstStage={isFirstStage} canManage={canManage} canRecord={canRecord} busy={busyId === o.id}
          expanded={openId === o.id} onToggle={() => setOpenId(openId === o.id ? null : o.id)}
          onAdvance={() => advance(o)} onAccept={() => setAcceptFor(o)} onBack={back} onExpedite={expedite} onPatch={(body) => patch(o, body)}
          onNote={(t) => note(o, t)} onNotes={(ord) => setNotesForId(ord.id)} onPay={(ord) => setPayForId(ord.id)} onRecord={(a) => record(o, a)} onEdit={onEdit} onSource={setSourcingFor} />
      ))}
      {acceptFor && <AcceptancePopup o={acceptFor} onClose={() => setAcceptFor(null)} onConfirm={confirmAccept} />}
      {sourcingFor && <EcotileFgSourcing order={sourcingFor} onClose={() => setSourcingFor(null)} onChanged={reload} />}
      {notesForId && (() => {
        const no = orders.find((x) => x.id === notesForId);
        return no ? <EcoNotesLightbox o={no} canManage={canManage} onNote={(t) => note(no, t)} onClose={() => setNotesForId(null)} /> : null;
      })()}
      {payForId && (() => {
        const po = orders.find((x) => x.id === payForId);
        return po ? <EcoPaymentLightbox o={po} canRecord={canRecord} canManage={canManage} onRecord={(a) => record(po, a)} onPatch={(b) => patch(po, b)} onClose={() => setPayForId(null)} /> : null;
      })()}
    </div>
  );
}

// ── PO line-items editor — catalogue browser + line rows w/ unit conversion ──
// Swiss/industrial: one always-open browser (search + thickness tabs + colour
// swatches), readable line rows, a per-line unit dropdown that converts price.
const GST_RATE = 0.18;
function lineAmount(li) { return Math.round((Number(li.qty) || 0) * (Number(li.offered_price) || 0) * 100) / 100; }
function lineTotals(items) {
  const subtotal = Math.round((items || []).reduce((s, li) => s + lineAmount(li), 0) * 100) / 100;
  const gst = Math.round(subtotal * GST_RATE * 100) / 100;
  const total = Math.round((subtotal + gst) * 100) / 100;
  return { subtotal, gst, total };
}
const r2 = (x) => Math.round((Number(x) || 0) * 100) / 100;
const BASE_UNIT_SHORT = { area: "m²", length: "m", each: "ea" };
const lineUT = (li) => li.ut || unitTypeFor(li.unit);

// always-open catalogue browser: search OR thickness/category tabs → swatch rows.
function ItemPicker({ onPick }) {
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("popular");
  const results = useMemo(() => searchCatalogue(q, q ? null : tab, 60), [q, tab]);
  return (
    <div className="rounded-lg border border-[hsl(var(--border))] overflow-hidden">
      <div className="p-2 border-b border-[hsl(var(--border))] bg-[hsl(var(--muted))]/30 space-y-2">
        <div className="flex items-center gap-2 h-8 px-2.5 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--background))] focus-within:border-[#E6B800]">
          <Search className="w-3.5 h-3.5 text-[hsl(var(--muted-foreground))] shrink-0" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search code or name — 7mm dark grey, ER7, ESD, install…"
            className="flex-1 bg-transparent outline-none text-[12.5px]" data-testid="eco-item-search" />
          {q && <button type="button" onClick={() => setQ("")} className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] text-[14px] leading-none">×</button>}
        </div>
        {!q && (
          <div className="flex items-center gap-1 flex-wrap" data-testid="eco-cat-tabs">
            {ECO_CAT_TABS.map((t) => (
              <button key={t.key} type="button" onClick={() => setTab(t.key)}
                className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-colors ${tab === t.key ? "bg-[#2b2b2b] border-[#2b2b2b] text-white dark:bg-white dark:border-white dark:text-[#1a1a1a]" : "bg-[hsl(var(--card))] border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"}`}>
                {t.label}
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="max-h-[212px] overflow-auto divide-y divide-[hsl(var(--border))]/50" data-testid="eco-item-results">
        {results.length === 0 ? (
          <div className="px-3 py-6 text-center text-[12px] text-[hsl(var(--muted-foreground))]">No match — try a colour or code, or add a custom line below.</div>
        ) : results.map((it) => (
          <button key={it.code} type="button" onClick={() => onPick(it)}
            className="w-full text-left px-2.5 py-1.5 flex items-center gap-2.5 hover:bg-[#FFCC00]/[0.09] transition-colors" data-testid={`eco-pick-${it.code}`}>
            <ProductGlyph code={it.code} hex={it.hex} tex={it.tex} />
            <div className="min-w-0 flex-1">
              <div className="text-[12px] leading-snug break-words">{it.name}</div>
              <div className="text-[10px] font-mono text-[hsl(var(--muted-foreground))] mt-0.5">{it.code}{it.note ? ` · ${it.note}` : ""}</div>
            </div>
            {it.popular && <span className="text-[11px] text-amber-500 shrink-0" title="Popular">★</span>}
            <span className="text-[11.5px] tabular-nums shrink-0 w-[68px] text-right text-[hsl(var(--muted-foreground))]">
              {it.list_price != null ? `${inr(it.list_price)}` : "—"}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

function LineItemsEditor({ items, onChange, hideTotals }) {
  const add = (it) => {
    const li = it
      ? { item_code: it.code, description: it.name, category: it.category || "", ut: it.ut || "each", unit: it.unit || "Each", qty: 1, list_price: it.list_price ?? null, offered_price: it.list_price ?? "", hex: it.hex || "", tex: it.tex || "", customer_erp_code: "" }
      : { item_code: "", description: "", category: "Custom", ut: "each", unit: "", qty: 1, list_price: null, offered_price: "", hex: "", tex: "", customer_erp_code: "" };
    onChange([...(items || []), li]);
  };
  const setLine = (i, patch) => { const next = items.slice(); next[i] = { ...next[i], ...patch }; onChange(next); };
  const rm = (i) => onChange(items.filter((_, j) => j !== i));
  // switching unit converts BOTH price and qty so the physical quantity + the
  // effective deal are preserved (amount unchanged): 1 tile = 0.25 m², etc.
  const changeUnit = (i, newUnit) => {
    const li = items[i]; const ut = lineUT(li);
    const fOld = unitFactor(ut, li.unit) || 1, fNew = unitFactor(ut, newUnit) || 1;
    const rate = Number(li.offered_price) || 0, qty = Number(li.qty) || 0;
    setLine(i, { unit: newUnit, offered_price: rate ? r2(rate * fNew / fOld) : li.offered_price, qty: qty ? r2(qty * fOld / fNew) : qty });
  };
  const { subtotal, gst, total } = lineTotals(items);
  const cell = "h-8 px-2 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--background))] text-[12px] outline-none focus:border-[#E6B800]";
  const has = (items || []).length > 0;
  return (
    <div data-testid="eco-lineitems" className="space-y-2">
      <ItemPicker onPick={add} />
      {has && (
        <div className="rounded-lg border border-[hsl(var(--border))] overflow-hidden">
          <div className="flex items-center gap-2 px-2.5 py-1.5 bg-[hsl(var(--muted))]/40 text-[9.5px] font-bold uppercase tracking-wide text-[hsl(var(--muted-foreground))]">
            <span className="flex-1">Item</span><span className="w-[104px]">Unit</span><span className="w-14 text-right">Qty</span><span className="w-24 text-right">Rate ₹</span><span className="w-24 text-right">Amount</span><span className="w-5" />
          </div>
          {items.map((li, i) => {
            const isCat = !!li.item_code; const ut = lineUT(li);
            return (
              <div key={i} className="flex items-start gap-2 px-2.5 py-2 border-t border-[hsl(var(--border))]" data-testid={`eco-line-${i}`}>
                {isCat ? (
                  <>
                    <ProductGlyph code={li.item_code} hex={li.hex} tex={li.tex} size={20} />
                    <div className="min-w-0 flex-1">
                      <div className="text-[12px] leading-snug break-words">{li.description}</div>
                      <div className="text-[10px] font-mono text-[hsl(var(--muted-foreground))] mt-0.5">{li.item_code}{li.list_price != null ? ` · MRP ${inr(li.list_price)} /${BASE_UNIT_SHORT[ut] || "ea"}` : ""}</div>
                    </div>
                  </>
                ) : (
                  <input className={`${cell} flex-1`} value={li.description} onChange={(e) => setLine(i, { description: e.target.value })} placeholder="custom item description" />
                )}
                {isCat && ut !== "each" ? (
                  <select className={`${cell} w-[104px]`} value={li.unit} onChange={(e) => changeUnit(i, e.target.value)} data-testid={`eco-unit-${i}`}>
                    {(UNIT_OPTIONS[ut] || []).map((o) => <option key={o.u} value={o.u}>{o.u}</option>)}
                  </select>
                ) : isCat ? (
                  <div className={`${cell} w-[104px] flex items-center text-[hsl(var(--muted-foreground))]`}>Each</div>
                ) : (
                  <input className={`${cell} w-[104px]`} value={li.unit} onChange={(e) => setLine(i, { unit: e.target.value })} placeholder="unit" />
                )}
                <input className={`${cell} w-14 text-right`} inputMode="decimal" value={li.qty} onChange={(e) => setLine(i, { qty: e.target.value })} />
                <input className={`${cell} w-24 text-right`} inputMode="decimal" value={li.offered_price} onChange={(e) => setLine(i, { offered_price: e.target.value })} placeholder="rate" />
                <div className="w-24 text-right text-[12px] tabular-nums font-medium pt-1.5">{inr(lineAmount(li))}</div>
                <button type="button" onClick={() => rm(i)} className="w-5 text-[hsl(var(--muted-foreground))] hover:text-red-500 text-[16px] leading-none pt-1" title="Remove line">×</button>
              </div>
            );
          })}
          {!hideTotals && (
            <div className="px-3 py-2 border-t border-[hsl(var(--border))] bg-[hsl(var(--muted))]/25 text-[12px] space-y-0.5">
              <div className="flex justify-between"><span className="text-[hsl(var(--muted-foreground))]">Subtotal (basic)</span><span className="tabular-nums font-medium">{inr(subtotal)}</span></div>
              <div className="flex justify-between"><span className="text-[hsl(var(--muted-foreground))]">GST 18%</span><span className="tabular-nums">{inr(gst)}</span></div>
              <div className="flex justify-between text-[13px] font-bold"><span>Total (incl.)</span><span className="tabular-nums" style={{ color: "#B78E10" }}>{inr(total)}</span></div>
            </div>
          )}
        </div>
      )}
      <div className="flex items-center gap-3">
        <button type="button" onClick={() => add(null)} className="text-[11.5px] font-medium text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]" data-testid="eco-custom-line">+ Custom line</button>
        {!has && <span className="text-[11px] text-[hsl(var(--muted-foreground))]">Pick items above to build the order — rates prefill from the list (indicative), switch units per line.</span>}
      </div>
    </div>
  );
}

// ── add / edit order form (create · update · delete) ─────────────────────────
const OTYPES = ["Project", "Supplementary", "Demo", "Install", "Freight"];
function Field({ label, children, span }) {
  return (
    <label className={span ? "col-span-2 block" : "block"}>
      <span className="text-[10px] font-semibold uppercase tracking-wide text-[hsl(var(--muted-foreground))]">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
function OrderForm({ initial, onClose, onSaved, orgNames }) {
  const editing = !!(initial && initial.id);
  const [lines, setLines] = useState(() => Array.isArray(initial?.line_items) ? initial.line_items.map((li) => ({ ...li })) : []);
  const hasLines = lines.length > 0;
  const { subtotal: liSub, total: liTotal } = lineTotals(lines);
  const [f, setF] = useState(() => ({
    organisation_name: initial?.organisation_name || "", order_type: initial?.order_type || "Project",
    po_number: initial?.po_number || "", po_value_basic: initial?.po_value_basic ?? "",
    po_received_date: initial?.po_received_date || "", advance_confirm_date: initial?.advance_confirm_date || "",
    dispatch_status: initial?.dispatch_status || "Pending", invoice_number: initial?.invoice_number || "",
    invoice_date: initial?.invoice_date || "", invoiced_value_basic: initial?.invoiced_value_basic ?? "",
    comments: initial?.comments || "", payment_status: initial?.payment_status || "Pending",
    total_value_incl: initial?.total_value_incl ?? "", received_so_far: initial?.received_so_far ?? "",
    deadline: initial?.deadline || "", followup_needed: !!initial?.followup_needed, followup_note: initial?.followup_note || "",
  }));
  const [busy, setBusy] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  // payment strip starts open only when it already carries data (editing)
  const [payOpen, setPayOpen] = useState(() => !!(initial && (Number(initial.total_value_incl) > 0 || Number(initial.received_so_far) > 0 || initial.payment_status === "Complete" || initial.advance_confirm_date)));
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  const num = (v) => (v === "" || v === null ? 0 : parseFloat(v) || 0);
  const d10 = (v) => (v ? String(v).slice(0, 10) : "");

  const save = async () => {
    if (!f.organisation_name.trim()) { toast.error("Organisation name is required"); return; }
    // clean line items → numeric qty/price + computed amount; when present they
    // drive the basic PO value (subtotal) and the all-inclusive total (+18% GST).
    const cleanLines = lines.map((li) => ({
      ...li, qty: num(li.qty),
      list_price: li.list_price == null ? null : num(li.list_price),
      offered_price: num(li.offered_price), amount: lineAmount(li),
    }));
    const body = {
      ...f, line_items: cleanLines,
      // the ENTERED PO value wins; line-item totals are only the fallback when
      // the field is left blank (Tushar: respect the entered PO value).
      po_value_basic: num(f.po_value_basic) || (hasLines ? liSub : 0), invoiced_value_basic: num(f.invoiced_value_basic),
      total_value_incl: num(f.total_value_incl) || (hasLines ? liTotal : 0), received_so_far: num(f.received_so_far),
      po_received_date: f.po_received_date || null, advance_confirm_date: f.advance_confirm_date || null,
      invoice_date: f.invoice_date || null, deadline: f.deadline || null,
    };
    setBusy(true);
    try {
      if (editing) { await axios.patch(`${API}/ecotile/orders/${initial.id}`, body); toast.success("Order updated"); }
      else { await axios.post(`${API}/ecotile/orders`, body); toast.success("Order added"); }
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save order"); }
    finally { setBusy(false); }
  };
  const del = async () => {
    setBusy(true);
    try { await axios.delete(`${API}/ecotile/orders/${initial.id}`); toast.success("Order deleted"); onSaved(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not delete"); }
    finally { setBusy(false); }
  };

  const inp = "w-full h-9 px-2.5 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--background))] text-sm outline-none focus:border-[#E6B800] focus:[box-shadow:0_0_0_3px_rgba(255,204,0,.15)]";
  // invoice / dispatch fields surface only once the order reaches Dispatch —
  // creating an order never shows them (Tushar: invoice is entered at Dispatch).
  const showInvoice = editing && ["Dispatch", "Completed"].includes(initial?.stage);
  // live footer strip — the ENTERED PO value always wins; lines are the fallback
  const enteredPO = num(f.po_value_basic);
  const baseVal = enteredPO || (hasLines ? liSub : 0);
  const gstVal = r2(baseVal * GST_RATE);
  const totIncl = num(f.total_value_incl) || (baseVal ? r2(baseVal + gstVal) : 0);
  const srcLabel = enteredPO ? "entered value (wins)" : hasLines ? `from ${lines.length} line${lines.length > 1 ? "s" : ""}` : "— enter or pick lines";
  const Sec = ({ children }) => (
    <div className="flex items-center gap-2 text-[10px] font-extrabold uppercase tracking-[0.12em] text-[hsl(var(--muted-foreground))] mb-2">
      {children}<span className="flex-1 h-px bg-[hsl(var(--border))]/70" />
    </div>
  );
  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center p-4 overflow-auto" style={{ background: "rgba(28,24,16,.34)" }} onClick={onClose}>
      <div className="rl-frost rl-hero-glass w-full max-w-[980px] rounded-2xl border border-[hsl(var(--border))] shadow-2xl mt-6 mb-10 overflow-hidden" onClick={(e) => e.stopPropagation()} data-testid="eco-order-form">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-[hsl(var(--border))]">
          <div className="font-bold text-[15px] flex items-center gap-2.5">
            <span className="w-[22px] h-[22px] rounded-md shrink-0" style={{ background: "linear-gradient(135deg,#FFCC00,#E6B800)", boxShadow: "inset 0 1px 0 rgba(255,255,255,.5)" }} />
            {editing ? "Edit order" : "New order"}{editing && <span className="text-[hsl(var(--muted-foreground))] font-normal">· #{initial.seq_no}</span>}
          </div>
          <button onClick={onClose} className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] text-lg leading-none px-1">✕</button>
        </div>

        {/* ── two panes: the ORDER (left) · the PRODUCTS (right) ── */}
        <div className="grid md:grid-cols-[340px_1fr]">
          <div className="px-5 py-4 md:border-r border-b md:border-b-0 border-[hsl(var(--border))]/70 bg-[hsl(var(--muted))]/20">
            <Sec>Order</Sec>
            <div className="space-y-2.5">
              <Field label="Organisation">
                <input className={inp} value={f.organisation_name} onChange={set("organisation_name")} placeholder="Client name, city" list="eco-org-names" autoComplete="off" autoFocus />
                <datalist id="eco-org-names">{(orgNames || []).map((n) => <option key={n} value={n} />)}</datalist>
              </Field>
              <Field label="Order type">
                <div className="flex flex-wrap gap-1.5" data-testid="eco-otype-pills">
                  {OTYPES.map((t) => (
                    <button key={t} type="button" onClick={() => setF((s) => ({ ...s, order_type: t }))}
                      className={`px-2.5 py-1 rounded-full text-[11.5px] font-semibold border transition-colors ${f.order_type === t ? "bg-[#2b2b2b] border-[#2b2b2b] text-white dark:bg-white dark:border-white dark:text-[#1a1a1a] [box-shadow:inset_0_2px_4px_rgba(0,0,0,.35)]" : "bg-[hsl(var(--card))] border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"}`}>
                      {t}
                    </button>
                  ))}
                </div>
              </Field>
              <div className="grid grid-cols-2 gap-2.5">
                <Field label="PO number"><input className={inp} value={f.po_number} onChange={set("po_number")} /></Field>
                <Field label="PO value ₹ (basic)"><input className={`${inp} text-right`} inputMode="decimal" value={f.po_value_basic} onChange={set("po_value_basic")} placeholder={hasLines ? "blank = from lines" : ""} title="Your entered value wins; leave blank to use the line-item subtotal" /></Field>
                <Field label="PO received"><input type="date" className={inp} value={d10(f.po_received_date)} onChange={set("po_received_date")} /></Field>
                <Field label="Deadline"><input type="date" className={inp} value={d10(f.deadline)} onChange={set("deadline")} /></Field>
              </div>
              <Field label="Comments"><textarea className={`${inp} h-14 py-2`} value={f.comments} onChange={set("comments")} placeholder="anything the team should know" /></Field>
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => setF((s) => ({ ...s, followup_needed: !s.followup_needed }))}
                  className={`shrink-0 px-2.5 py-1.5 rounded-full text-[11.5px] font-semibold border transition-colors ${f.followup_needed ? "bg-[#8A6FB8]/15 border-[#8A6FB8]/40 text-[#6d54a3] dark:text-[#b39ddb] [box-shadow:inset_0_1px_3px_rgba(0,0,0,.12)]" : "bg-[hsl(var(--card))] border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"}`}
                  data-testid="eco-fu-chip">⚑ Follow-up</button>
                <input className={`${inp} flex-1 h-8 text-[12px]`} placeholder="note (optional)" value={f.followup_note} onChange={set("followup_note")} />
              </div>
              {showInvoice && (
                <div className="pt-2">
                  <Sec>Dispatch · invoice</Sec>
                  <div className="grid grid-cols-2 gap-2.5">
                    <Field label="Dispatch status"><select className={inp} value={f.dispatch_status} onChange={set("dispatch_status")}><option>Pending</option><option>Complete</option></select></Field>
                    <Field label="Invoice number"><input className={inp} value={f.invoice_number} onChange={set("invoice_number")} /></Field>
                    <Field label="Invoice date"><input type="date" className={inp} value={d10(f.invoice_date)} onChange={set("invoice_date")} /></Field>
                    <Field label="Invoiced ₹ (basic)"><input className={`${inp} text-right`} inputMode="decimal" value={f.invoiced_value_basic} onChange={set("invoiced_value_basic")} /></Field>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="px-5 py-4">
            <Sec>Products <span className="font-medium normal-case tracking-normal">— optional · drives value when PO ₹ left blank</span></Sec>
            <LineItemsEditor items={lines} onChange={setLines} hideTotals />
          </div>
        </div>

        {/* ── payment: collapsed accordion strip ── */}
        <div className="border-t border-[hsl(var(--border))]/70" data-testid="eco-pay-strip">
          <button type="button" onClick={() => setPayOpen((v) => !v)}
            className="w-full flex items-center gap-2 px-5 py-2.5 text-[10.5px] font-extrabold uppercase tracking-[0.1em] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors">
            <span className={`transition-transform duration-200 ${payOpen ? "rotate-90" : ""}`}>▸</span>
            Payment · all-inclusive
            <span className="ml-auto font-medium normal-case tracking-normal text-[10.5px]">optional — opening balance / received so far</span>
          </button>
          {payOpen && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 px-5 pb-4">
              <Field label="Total value ₹ (incl.)"><input className={`${inp} text-right`} inputMode="decimal" value={f.total_value_incl} onChange={set("total_value_incl")} placeholder={hasLines ? "blank = lines + GST" : ""} title="Your entered value wins; leave blank to use line subtotal + 18% GST" /></Field>
              <Field label="Received so far ₹"><input className={`${inp} text-right`} inputMode="decimal" value={f.received_so_far} onChange={set("received_so_far")} /></Field>
              <Field label="Payment status"><select className={inp} value={f.payment_status} onChange={set("payment_status")}><option>Pending</option><option>Complete</option></select></Field>
              <Field label="Advance confirmed"><input type="date" className={inp} value={d10(f.advance_confirm_date)} onChange={set("advance_confirm_date")} /></Field>
              {editing && <div className="col-span-2 md:col-span-4 text-[11px] text-[hsl(var(--muted-foreground))]">Setting “received so far” here writes it directly. To log a dated payment on the ledger, use “Record payment” on the row instead.</div>}
            </div>
          )}
        </div>

        {/* ── sticky footer: live value strip + actions ── */}
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 px-5 py-3 border-t border-[hsl(var(--border))] bg-[hsl(var(--muted))]/25">
          <div className="flex items-baseline gap-5 flex-1 flex-wrap" data-testid="eco-form-values">
            <div className="flex flex-col">
              <span className="text-[9px] font-extrabold uppercase tracking-[0.1em] text-[hsl(var(--muted-foreground))]">PO value (basic)</span>
              <span className="text-[16px] font-extrabold tabular-nums leading-tight" style={{ color: "#B78E10" }}>{inr(baseVal)}</span>
              <span className="text-[9.5px] text-[hsl(var(--muted-foreground))]">{srcLabel}</span>
            </div>
            <div className="flex flex-col">
              <span className="text-[9px] font-extrabold uppercase tracking-[0.1em] text-[hsl(var(--muted-foreground))]">GST 18%</span>
              <span className="text-[16px] font-bold tabular-nums leading-tight">{inr(gstVal)}</span>
            </div>
            <div className="flex flex-col">
              <span className="text-[9px] font-extrabold uppercase tracking-[0.1em] text-[hsl(var(--muted-foreground))]">Total (incl.)</span>
              <span className="text-[16px] font-bold tabular-nums leading-tight">{inr(totIncl)}</span>
            </div>
            <div className="flex flex-col">
              <span className="text-[9px] font-extrabold uppercase tracking-[0.1em] text-[hsl(var(--muted-foreground))]">Lines</span>
              <span className="text-[16px] font-bold tabular-nums leading-tight">{lines.length}</span>
            </div>
            {editing && (
              confirmDel ? (
                <div className="flex items-center gap-2 text-[12px]">
                  <span className="text-[hsl(var(--muted-foreground))]">Delete this order &amp; its payments?</span>
                  <button onClick={del} disabled={busy} className="font-bold text-red-600 dark:text-red-400 hover:underline">Yes, delete</button>
                  <button onClick={() => setConfirmDel(false)} disabled={busy} className="text-[hsl(var(--muted-foreground))] hover:underline">Cancel</button>
                </div>
              ) : (
                <button onClick={() => setConfirmDel(true)} disabled={busy} className="text-[12px] text-red-600 dark:text-red-400 hover:underline self-center">Delete order</button>
              )
            )}
          </div>
          <div className="flex gap-2 ml-auto">
            <button onClick={onClose} className="h-9 px-4 rounded-full text-[13px] font-medium border border-[hsl(var(--border))]">Cancel</button>
            <button onClick={save} disabled={busy} className="rl-lit-btn h-9 px-5 rounded-full text-[13px] font-extrabold disabled:opacity-50"
              style={{ background: "linear-gradient(180deg,#FFD633,#EFBF00)", color: "#2b2500", boxShadow: "inset 0 1px 0 rgba(255,255,255,.5), 0 2px 5px rgba(180,140,0,.35)" }}>
              {editing ? "Save changes" : "Add order"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── live activity feed (borrows the metallic Dashboard LiveUpdatesCard DNA) ──
// relative time — same vocabulary as components/dashboard/LiveUpdatesCard.js
function ecoTimeAgo(ts) {
  const s = (Date.now() - new Date(ts).getTime()) / 1000;
  if (!Number.isFinite(s)) return "";
  if (s < 5) return "just now";
  if (s < 60) return `${Math.floor(s)}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 10800) return `${Math.floor(s / 3600)}h`;
  const d = new Date(ts), today = new Date();
  if (d.toDateString() === today.toDateString()) return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  return `${String(d.getDate()).padStart(2, "0")}-${d.toLocaleString("en-US", { month: "short" })}`;
}
function ecoShortName(u) {
  if (!u) return "Someone";
  const base = String(u).replace(/\s*\(.*\)/, "").replace(/@.*/, "").trim();
  return base ? base.split(" ")[0] : "Someone";
}
// the one stage badge for the feed — glassy hairline pill, stage-tinted
function ActStagePill({ stage }) {
  const M = STAGE_META[stage];
  if (!M) return null;
  const Icon = M.Icon;
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full border rl-pill leading-none whitespace-nowrap"
      style={{ background: `${M.color}0d`, color: M.color, borderColor: `${M.color}59` }}>
      <Icon className="w-2.5 h-2.5" />{M.short}
    </span>
  );
}
const CLAMP2 = { display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" };

function ActivityRow({ e, onOpen }) {
  let Icon, tint, verb;
  if (e.kind === "stage") {
    const M = STAGE_META[e.to_stage] || STAGE_META["PO Received"];
    Icon = M.Icon; tint = M.color; verb = e.skipped_mfg ? "moved to" : "advanced to";
  } else if (e.kind === "payment") {
    Icon = IndianRupee; tint = "#B78E10"; verb = "recorded a payment";
  } else {
    Icon = MessageSquare; tint = "#7c8794"; verb = "added a note";
  }
  return (
    <button type="button" onClick={() => onOpen(e)} data-testid={`eco-act-${e.kind}-${e.order_id}`}
      className="w-full text-left flex items-start gap-2.5 px-3.5 py-2.5 hover:bg-[hsl(var(--muted))]/30 transition-colors focus-visible:outline-none focus-visible:bg-[hsl(var(--muted))]/40">
      <span className="mt-0.5 w-7 h-7 rounded-full flex items-center justify-center shrink-0 rl-well" style={{ background: `${tint}16`, color: tint }}>
        <Icon className="w-3.5 h-3.5" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-1.5">
          <span className="text-[12px] font-semibold truncate" title={e.organisation_name}>{e.organisation_name}</span>
          {e.seq_no != null && <span className="text-[10px] text-[hsl(var(--muted-foreground))]/70 shrink-0">#{e.seq_no}</span>}
          <span className="ml-auto text-[10px] text-[hsl(var(--muted-foreground))]/70 shrink-0 tabular-nums" title={new Date(e.at).toLocaleString()}>{ecoTimeAgo(e.at)}</span>
        </div>
        <div className="text-[11.5px] text-[hsl(var(--muted-foreground))] flex items-center gap-1.5 flex-wrap mt-0.5">
          <span className="font-medium text-[hsl(var(--foreground))]/75">{ecoShortName(e.by)}</span>
          <span>{verb}</span>
          {e.kind === "stage" && <ActStagePill stage={e.to_stage} />}
          {e.kind === "stage" && e.skipped_mfg && <span className="text-[10px] text-amber-600 dark:text-amber-400">skipped mfg</span>}
          {e.kind === "payment" && (e.amount != null
            ? <span className="font-semibold text-emerald-600 dark:text-emerald-400 tabular-nums">{inr(e.amount)}</span>
            : <span className="text-[hsl(var(--muted-foreground))]">•••</span>)}
        </div>
        {e.kind === "note" && e.text && (
          <div className="text-[11.5px] mt-1 rounded-md bg-[hsl(var(--muted))]/40 px-2 py-1 break-words" style={CLAMP2}>{e.text}</div>
        )}
      </div>
    </button>
  );
}

function ActivityFeed({ events, onOpen }) {
  const list = events || [];
  return (
    <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] rl-glass-edge overflow-hidden flex flex-col" data-testid="eco-activity-feed">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-[hsl(var(--border))]">
        <Activity className="w-4 h-4" style={{ color: "#B78E10" }} />
        <div className="text-[13px] font-bold">Live activity</div>
        {list.length > 0 && <div className="ml-auto text-[11px] text-[hsl(var(--muted-foreground))]">{list.length} recent</div>}
      </div>
      <div className="divide-y divide-[hsl(var(--border))]/60 overflow-auto" style={{ maxHeight: 560 }}>
        {list.length === 0 ? (
          <div className="px-4 py-12 text-center text-[hsl(var(--muted-foreground))]">
            <Activity className="w-6 h-6 mx-auto mb-2 opacity-40" />
            <div className="text-[13px] font-medium text-[hsl(var(--foreground))]/80 mb-1">No activity yet</div>
            <div className="text-[12px] max-w-[260px] mx-auto">Stage moves, notes, and recorded payments show up here as the team works orders.</div>
          </div>
        ) : list.map((e, i) => <ActivityRow key={`${e.kind}-${e.order_id}-${e.at}-${i}`} e={e} onOpen={onOpen} />)}
      </div>
    </div>
  );
}

// ── history log — full-page activity stream (mirrors the metallic History Log) ──
function HistoryView({ activity, orders, openOrder, onBack }) {
  const [rows, setRows] = useState(activity || []);
  const [kind, setKind] = useState("all");   // all | stage | payment | note
  const [hq, setHq] = useState("");
  useEffect(() => {
    let live = true;
    axios.get(`${API}/ecotile/activity`, { params: { fy: FY_DEFAULT, limit: 400 } })
      .then((r) => { if (live) setRows(r.data.events || []); }).catch(() => {});
    return () => { live = false; };
  }, []);
  const ql = hq.trim().toLowerCase();
  const shown = useMemo(() => (rows || [])
    .filter((e) => kind === "all" || e.kind === kind)
    .filter((e) => !ql || `${e.organisation_name} ${e.by} ${e.text || ""}`.toLowerCase().includes(ql)), [rows, kind, ql]);
  const KINDS = [["all", "All", Activity], ["stage", "Stage moves", ArrowRight], ["payment", "Payments", IndianRupee], ["note", "Notes", MessageSquare]];
  const exportLog = () => {
    const head = "When,Who,Organisation,PO,Type,Detail";
    const cell = (v) => { const s = v == null ? "" : String(v); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
    const body = shown.map((e) => [e.at ? new Date(e.at).toISOString() : "", e.by || "", e.organisation_name || "", e.po_number || "", e.kind, e.kind === "stage" ? e.to_stage : e.kind === "payment" ? (e.amount ?? "") : (e.text || "")].map(cell).join(",")).join("\n");
    const blob = new Blob([head + "\n" + body], { type: "text/csv" });
    const url = URL.createObjectURL(blob); const a = document.createElement("a");
    a.href = url; a.download = `ecotile_history_${new Date().toISOString().slice(0, 10)}.csv`; a.click(); URL.revokeObjectURL(url);
  };
  return (
    <div className="mt-4 rounded-xl rl-glass-edge overflow-hidden bg-[hsl(var(--card))] border border-[hsl(var(--border))]" data-testid="eco-history-view">
      <div className="h-1" style={{ background: "linear-gradient(90deg, #B78E10, rgba(183,142,16,.35))" }} />
      <div className="flex items-center gap-3 px-5 pt-4 pb-3 flex-wrap">
        <button onClick={onBack} className="w-8 h-8 rounded-full rl-well border border-transparent hover:border-[hsl(var(--border))] flex items-center justify-center text-[hsl(var(--muted-foreground))]" title="Back to dashboard"><ArrowLeft className="w-4 h-4" /></button>
        <Clock className="w-5 h-5 text-[#B78E10]" />
        <h2 className="text-[20px] font-bold tracking-[-0.01em]">History log</h2>
        <span className="text-[13px] text-[hsl(var(--muted-foreground))]">{shown.length} events</span>
        <div className="ml-auto flex items-center gap-2">
          <label className="flex items-center gap-2 w-[230px] h-9 px-3 rounded-[10px] border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/40 rl-search">
            <Search className="w-3.5 h-3.5 text-[hsl(var(--muted-foreground))]" />
            <input value={hq} onChange={(e) => setHq(e.target.value)} placeholder="Search history…" className="flex-1 min-w-0 bg-transparent outline-none text-[12.5px]" />
          </label>
          <button onClick={exportLog} className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full rl-well border border-[hsl(var(--border))] text-[12px] font-semibold text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] hover:border-[#E6B800]"><Download className="w-3.5 h-3.5" /> CSV</button>
        </div>
      </div>
      <div className="flex items-center gap-2 px-5 pb-3 flex-wrap">
        {KINDS.map(([k, label, Ic]) => (
          <button key={k} onClick={() => setKind(k)}
            className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-[12px] font-semibold border transition-colors ${kind === k ? "bg-[#FFCC00] border-[#E6B800] text-[#2b2500]" : "rl-well border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"}`}>
            <Ic className="w-3.5 h-3.5" /> {label}
          </button>
        ))}
      </div>
      <div className="border-t border-[hsl(var(--border))] divide-y divide-[hsl(var(--border))]/60">
        {shown.length === 0 ? (
          <div className="px-4 py-14 text-center text-[hsl(var(--muted-foreground))]"><Clock className="w-7 h-7 mx-auto mb-2 opacity-30" /><div className="text-[13px]">No matching history.</div></div>
        ) : shown.map((e, i) => <ActivityRow key={`${e.kind}-${e.order_id}-${e.at}-${i}`} e={e} onOpen={(ev) => openOrder(ev.order_id)} />)}
      </div>
    </div>
  );
}

// ── dashboard hero (borrows the metallic StatCards widget) ────────────────────
// One white glass card: an inline stat strip on top + a PIPELINE row of
// stage-washed cards with count/value + a 7-day inflow sparkline.
function EcoStat({ label, value, sub, color, onClick }) {
  const clickable = typeof onClick === "function";
  const inner = (
    <div className="flex flex-col gap-0.5">
      <span className="flex items-center gap-1.5">
        {clickable && <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: color }} />}
        <span className="text-[10px] font-medium uppercase tracking-[0.08em]" style={{ color: clickable ? color : "hsl(var(--muted-foreground) / 0.7)" }}>{label}</span>
      </span>
      <span className="text-[1.5rem] font-bold font-mono-data tracking-tight leading-none tabular-nums" style={{ color: clickable ? color : "hsl(var(--foreground))" }}>{value}</span>
      {sub && <span className="text-[10px] text-[hsl(var(--muted-foreground))] leading-none">{sub}</span>}
    </div>
  );
  return clickable
    ? <button type="button" onClick={onClick} className="text-left hover:opacity-80 transition-opacity">{inner}</button>
    : <div>{inner}</div>;
}

function EcoSpark({ data, color, width = 64, height = 22 }) {
  if (!Array.isArray(data) || data.length < 2) return null;
  const min = Math.min(...data), max = Math.max(...data), range = max - min || 1;
  const padX = 1, padY = 2, innerW = width - padX * 2, innerH = height - padY * 2, step = innerW / (data.length - 1);
  const pts = data.map((v, i) => [padX + i * step, padY + innerH - ((v - min) / range) * innerH]);
  const d = pts.map(([x, y], i) => (i ? `L${x},${y}` : `M${x},${y}`)).join(" ");
  const last = pts[pts.length - 1];
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} style={{ overflow: "visible" }} aria-hidden="true">
      <line x1={padX} x2={width - padX} y1={padY + innerH} y2={padY + innerH} stroke="hsl(var(--muted-foreground))" strokeOpacity="0.25" strokeWidth="1" />
      <path d={d} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={last[0]} cy={last[1]} r="1.8" fill={color} />
    </svg>
  );
}

function EcoHero({ summary, orders, stageCounts, go, goRegister }) {
  const hideVal = summary.value_hidden;
  const money = (v) => (hideVal ? "•••" : inrShort(v));
  // per-stage booked value + 7-day inflow trend (from stage_history)
  const { stageValue, stageTrend, maxCount } = useMemo(() => {
    const sv = {}, days = []; const now = new Date(); now.setHours(0, 0, 0, 0);
    for (let i = 6; i >= 0; i--) { const d = new Date(now); d.setDate(d.getDate() - i); days.push(d.getTime()); }
    const trend = {}; NAV_STAGES.forEach((s) => { sv[s] = 0; trend[s] = days.map(() => 0); });
    orders.forEach((o) => {
      const st = o.stage || "PO Received"; if (sv[st] != null) sv[st] += Number(o.po_value_basic) || 0;
      (o.stage_history || []).forEach((h) => {
        const to = h.to || h.to_stage; if (!to || trend[to] == null || !h.at) return;
        const t = new Date(String(h.at).slice(0, 10) + "T00:00:00").getTime();
        const di = days.indexOf(t); if (di >= 0) trend[to][di]++;
      });
    });
    return { stageValue: sv, stageTrend: trend, maxCount: Math.max(1, ...NAV_STAGES.map((s) => stageCounts[s] || 0)) };
  }, [orders, stageCounts]);

  return (
    <div className="rounded-2xl rl-glass rl-hero-glass border border-[hsl(var(--border))] px-5 py-4" data-testid="eco-hero">
      {/* inline stat strip */}
      <div className="flex flex-wrap items-center gap-x-6 gap-y-3 divide-x divide-[hsl(var(--border))]">
        <div className="pr-0"><EcoStat label="Orders" value={summary.orders} sub={`${summary.invoiced_count} invoiced`} /></div>
        <div className="pl-6"><EcoStat label="Booked (basic)" value={money(summary.booked_basic)} /></div>
        <div className="pl-6"><EcoStat label="Collected" value={money(summary.collected)} /></div>
        <div className="pl-6"><EcoStat label="Overdue" value={summary.overdue?.count ?? 0} sub={hideVal ? "" : inrShort(summary.overdue?.amount || 0)} color="#ef4444" onClick={() => goRegister("overdue")} /></div>
        <div className="pl-6"><EcoStat label="No deadline" value={summary.no_deadline?.count ?? 0} sub={hideVal ? "" : inrShort(summary.no_deadline?.amount || 0)} color="#f59e0b" onClick={() => goRegister("nodl")} /></div>
      </div>

      <div className="border-t border-[hsl(var(--border))] my-3.5" />
      <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-[hsl(var(--muted-foreground))]/70 mb-2">Pipeline</div>

      <div className="flex gap-2.5 overflow-x-auto pb-0.5">
        {NAV_STAGES.map((s) => {
          const M = STAGE_META[s]; const c = stageCounts[s] || 0;
          const ratio = c / maxCount;
          const washHex = c > 0 ? Math.round((0.05 + 0.30 * Math.pow(ratio, 0.7)) * 255).toString(16).padStart(2, "0") : "00";
          const trend = stageTrend[s];
          return (
            <button key={s} onClick={() => go(s)} data-testid={`eco-pipe-${s.replace(/\s/g, "-").toLowerCase()}`}
              className="rl-pipeline-card relative flex flex-col h-[104px] flex-1 min-w-[150px] rounded-lg overflow-hidden text-left group bg-[hsl(var(--card))]"
              style={{ backgroundImage: `radial-gradient(95% 110% at 0% 0%, ${M.color}${washHex}, transparent 70%)` }}>
              <div className="absolute left-0 top-0 bottom-0 w-[3px] z-[3]" style={{ background: M.color }} />
              <div className="flex items-center justify-between gap-1 px-3 pt-2.5">
                <span className="text-[11px] font-semibold" style={{ color: M.color }}>{M.short}</span>
                <ChevronRight className="w-3 h-3 shrink-0 opacity-40 group-hover:opacity-80 group-hover:translate-x-0.5 transition-all" style={{ color: M.color }} />
              </div>
              <div className="flex-1 relative">
                <div className="absolute bottom-2.5 left-3 right-3 flex items-end justify-between gap-1">
                  <div className="flex flex-col gap-0.5">
                    <div className="flex items-baseline gap-1.5">
                      <span className="text-2xl font-bold font-mono-data leading-none">{c}</span>
                      <span className="text-[10px] text-[hsl(var(--muted-foreground))] leading-none pb-0.5">orders</span>
                    </div>
                    {!hideVal && <span className="text-[10px] font-mono-data text-[hsl(var(--muted-foreground))] tabular-nums">{inrShort(stageValue[s] || 0)}</span>}
                  </div>
                  <div className="opacity-70 group-hover:opacity-100 transition-opacity shrink-0"><EcoSpark data={trend} color={M.color} /></div>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── dashboard (home) — hero + pipeline-at-a-glance + activity + payments + targets ──
const ECO_SHOW_TARGETS_KEY = "rocklink.ecotile.showTargets";
function DashboardView({ summary, targets, orders, stageCounts, activity, dark, go, goRegister, openOrder, canManage, onImport, onExport }) {
  const [showTargets, setShowTargets] = useState(() => {
    try { return localStorage.getItem(ECO_SHOW_TARGETS_KEY) !== "0"; } catch { return true; }
  });
  const toggleTargets = () => setShowTargets((v) => {
    const nv = !v;
    try { localStorage.setItem(ECO_SHOW_TARGETS_KEY, nv ? "1" : "0"); } catch { /* ignore */ }
    return nv;
  });
  if (!summary) return null;
  const hideVal = summary.value_hidden;
  const monthly = (targets?.months || []).map((m, i) => ({
    m, target: targets.monthly_target_lakh[i], invoiced: targets.monthly_invoiced_lakh[i], booked: targets.monthly_booked_lakh[i],
  }));
  const cInv = dark ? "#B78E10" : "#A67F00", cBook = dark ? "#22A377" : "#1D8F6B", cTgt = dark ? "#c9cfcd" : "#3F4444";
  const fyTarget = targets?.cumulative_target_lakh?.slice(-1)[0] || 0;
  const invSoFar = (targets?.cumulative_invoiced_lakh || []).slice(-1)[0] || 0;
  const pct = fyTarget ? (invSoFar / fyTarget * 100) : 0;
  return (
    <div className="space-y-4">
      <EcoHero summary={summary} orders={orders} stageCounts={stageCounts} go={go} goRegister={goRegister} />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">
        {/* left — live activity feed */}
        <div className="lg:col-span-1">
          <ActivityFeed events={activity} onOpen={(e) => openOrder(e.order_id)} />
        </div>
        {/* right — payments + targets stacked */}
        <div className="lg:col-span-2 space-y-4">
          <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4">
            <div className="flex items-center justify-between mb-2"><div className="text-[13px] font-bold">Payments</div><button onClick={() => go("payments")} className="text-[11px] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]">Manage →</button></div>
            <div className="flex gap-2">
              {[["overdue", "Overdue", "#c22f2f"], ["no_deadline", "No deadline", "#E6B800"], ["upcoming", "Upcoming", "#94a3b8"]].map(([k, l, col]) => (
                <div key={k} className="flex-1 rounded-lg p-2.5" style={{ background: `${col}14` }}>
                  <div className="text-[11px] text-[hsl(var(--muted-foreground))]">{l}</div>
                  <div className="text-[15px] font-extrabold" style={{ color: col }}>{summary[k]?.count ?? 0}</div>
                  <div className="text-[10.5px] text-[hsl(var(--muted-foreground))]">{hideVal ? "•••" : inrShort(summary[k]?.amount || 0)}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4">
            <div className="flex items-center justify-between mb-1">
              <div className="text-[13px] font-bold">Targets <span className="text-[11px] font-normal text-[hsl(var(--muted-foreground))]">· {pct.toFixed(0)}% of ₹{(fyTarget / 100).toFixed(2)} Cr</span></div>
              <div className="flex items-center gap-2.5">
                <button onClick={toggleTargets} title={showTargets ? "Hide the target chart" : "Show the target chart"}
                  className="inline-flex items-center gap-1 text-[11px] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]">
                  {showTargets ? <><EyeOff className="w-3 h-3" /> Hide</> : <><Eye className="w-3 h-3" /> Show</>}
                </button>
                {showTargets && <button onClick={() => go("targets")} className="text-[11px] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]">Details →</button>}
              </div>
            </div>
            {showTargets ? (
              <ResponsiveContainer width="100%" height={150}>
                <ComposedChart data={monthly} margin={{ top: 6, right: 6, bottom: 0, left: -18 }}>
                  <CartesianGrid strokeDasharray="2 2" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="m" tick={{ fontSize: 9, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} interval={0} />
                  <YAxis tick={{ fontSize: 9, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ borderRadius: 8, fontSize: 11, border: "1px solid hsl(var(--border))", background: "hsl(var(--card))" }} formatter={(v, n) => [v ? v.toFixed(1) + " L" : "—", n]} />
                  <Bar dataKey="invoiced" name="Invoiced" fill={cInv} radius={[2, 2, 0, 0]} maxBarSize={12} />
                  <Bar dataKey="booked" name="Booked" fill="none" stroke={cBook} strokeWidth={1.3} radius={[2, 2, 0, 0]} maxBarSize={12} />
                  <Line dataKey="target" name="Target" stroke={cTgt} strokeWidth={1.6} strokeDasharray="4 2" dot={false} />
                </ComposedChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-[11.5px] text-[hsl(var(--muted-foreground))] py-2">Chart hidden. <button onClick={toggleTargets} className="underline hover:text-[hsl(var(--foreground))]">Show it</button> or open <button onClick={() => go("targets")} className="underline hover:text-[hsl(var(--foreground))]">full targets</button>.</div>
            )}
          </div>
        </div>
      </div>

      {/* ── dashboard footer — module meta + data import/export ── */}
      <div className="mt-2 rounded-xl rl-glass-edge border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-4 py-3 flex flex-wrap items-center gap-x-5 gap-y-2">
        <div className="flex items-center gap-2">
          <span className="flex items-center justify-center w-7 h-7 rounded-lg shrink-0" style={{ background: "var(--eco-yellow, #FFCC00)", boxShadow: "inset 0 1px 0 rgba(255,255,255,.5)" }}>
            <span className="font-[Outfit] font-extrabold text-[13px] text-[#2b2500]">E</span>
          </span>
          <div className="leading-tight">
            <div className="text-[12px] font-bold">RockLink · Ecotile <span className="font-normal text-[hsl(var(--muted-foreground))]">v1</span></div>
            <div className="text-[10.5px] text-[hsl(var(--muted-foreground))]">FY {FY_DEFAULT} · Asia-Pacific &amp; MENA</div>
          </div>
        </div>
        <div className="hidden sm:flex items-center gap-4 text-[11px] text-[hsl(var(--muted-foreground))] font-mono-data tabular-nums">
          <span><span className="font-bold text-[hsl(var(--foreground))]">{orders.length}</span> orders</span>
          <span><span className="font-bold text-[hsl(var(--foreground))]">{stageCounts["Completed"] || 0}</span> completed</span>
          <span><span className="font-bold text-[hsl(var(--foreground))]">{summary?.orders != null ? (summary.invoiced_count ?? "—") : "—"}</span> invoiced</span>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <button onClick={onExport} data-testid="eco-export"
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full rl-well border border-[hsl(var(--border))] text-[12px] font-semibold text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] hover:border-[#E6B800] transition-colors">
            <Download className="w-3.5 h-3.5" /> Export CSV
          </button>
          {canManage && (
            <button onClick={onImport} data-testid="eco-import-open"
              className="inline-flex items-center gap-1.5 h-8 px-3.5 rounded-full rl-lit-btn text-[12px] font-bold text-white transition-colors" style={{ backgroundColor: "#B78E10" }}>
              <Upload className="w-3.5 h-3.5" /> Import
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── page ─────────────────────────────────────────────────────────────────────

export default function EcotilePage() {
  const { user } = useContext(AuthContext);
  const [view, setView] = useState("dashboard"); // "dashboard" | <stage> | "register" | "payments" | "targets"
  const [modOpen, setModOpen] = useState(false); // Modules waffle menu
  const [summary, setSummary] = useState(null);
  const [orders, setOrders] = useState([]);
  const [targets, setTargets] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [openId, setOpenId] = useState(null);
  const [activity, setActivity] = useState([]);
  const [formFor, setFormFor] = useState(null); // null | "new" | <order to edit>
  const [importOpen, setImportOpen] = useState(false);
  const [stageFocus, setStageFocus] = useState(null); // { stage, id, q } from magic search
  const stageCounts = useMemo(() => {
    const c = {}; NAV_STAGES.forEach((s) => (c[s] = 0));
    orders.forEach((o) => { const s = o.stage || "PO Received"; if (c[s] !== undefined) c[s]++; });
    return c;
  }, [orders]);
  // distinct organisation names for the new/edit form's client autocomplete
  const orgNames = useMemo(() => Array.from(new Set(orders.map((o) => o.organisation_name).filter(Boolean))).sort(), [orders]);

  const isAdmin = user?.role === "Admin";
  const canManage = isAdmin || !!user?.permissions?.can_manage_ecotile_orders;
  const canRecord = isAdmin || !!user?.permissions?.can_record_ecotile_payments;
  const dark = typeof document !== "undefined" && document.documentElement.classList.contains("dark");

  const load = useCallback(async () => {
    setErr("");
    try {
      const [s, o, t, a] = await Promise.all([
        axios.get(`${API}/ecotile/summary`, { params: { fy: FY_DEFAULT } }),
        axios.get(`${API}/ecotile/orders`, { params: { fy: FY_DEFAULT } }),
        axios.get(`${API}/ecotile/targets`, { params: { fy: FY_DEFAULT } }),
        // activity feed is non-critical — degrade to empty rather than break the page
        axios.get(`${API}/ecotile/activity`, { params: { fy: FY_DEFAULT, limit: 40 } }).catch(() => ({ data: { events: [] } })),
      ]);
      setSummary(s.data); setOrders(o.data.orders || []); setTargets(t.data); setActivity(a.data.events || []);
    } catch (e) {
      setErr(e?.response?.status === 403 ? "You don’t have access to the Ecotile module." : "Could not load Ecotile data.");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // header (shell) → page bridge: magic search / history button dispatch events
  useEffect(() => ecoBus.on((d) => {
    if (d.type === "view") setView(d.view);
    else if (d.type === "open") { setFilter("all"); setQ(""); setOpenId(d.id); setView("register"); }
    else if (d.type === "openInStage") { setStageFocus({ stage: d.stage, id: d.id, q: d.q }); setView(d.stage); }
    else if (d.type === "registerFilter") { setFilter(d.filter); setQ(""); setView("register"); }
  }), []);

  if (loading) return <div className="py-20 text-center text-[hsl(var(--muted-foreground))]">Loading Ecotile…</div>;
  if (err) return <div className="py-20 text-center"><div className="text-[15px] font-semibold mb-1">{err}</div><button onClick={load} className="mt-2 px-3 py-1.5 rounded-full border border-[hsl(var(--border))] text-sm">Retry</button></div>;

  const isStage = NAV_STAGES.includes(view);
  const goRegister = (f) => { if (f) setFilter(f); setView("register"); };
  // jump from an activity row straight to that order, opened in the register
  const openOrder = (id) => { setFilter("all"); setQ(""); setOpenId(id); setView("register"); };
  const secBtn = (v) => `h-8 px-3 rounded-full text-[12px] font-bold border transition-colors ${view === v ? "bg-[#FFCC00] border-[#E6B800] text-[#2b2500]" : "border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"}`;
  return (
    <div className="space-y-3" data-testid="ecotile-page">
      {/* primary nav — Dashboard home + chevron pipeline spine + secondary access (like RockLink) */}
      <div className="flex items-center gap-2 flex-wrap py-1">
        {/* Dashboard button — EXACT metallic recipe (rl-dash-glass rainbow when
            active, rl-nav-glass frosted at rest) */}
        <button onClick={() => setView("dashboard")} data-testid="eco-nav-dashboard"
          className={`inline-flex items-center gap-1.5 px-3.5 h-[42px] rounded-md text-[13px] font-semibold shrink-0 transition ${view === "dashboard" ? "text-white rl-dash-glass" : "rl-nav-glass text-[hsl(var(--muted-foreground))]"}`}
          style={{ fontFamily: "var(--rl-font-heading), sans-serif", ...(view === "dashboard" ? { backgroundColor: "#fbbf24", backgroundImage: "radial-gradient(60% 130% at 20% 30%, #a855f7 0%, transparent 55%), radial-gradient(70% 130% at 75% 25%, #ef4444 0%, transparent 55%), radial-gradient(80% 140% at 55% 95%, #ec4899 0%, transparent 55%), radial-gradient(70% 130% at 10% 90%, #38bdf8 0%, transparent 55%), radial-gradient(60% 120% at 90% 85%, #f59e0b 0%, transparent 55%)", textShadow: "0 1px 2px rgba(0,0,0,0.3)" } : {})}}>
          <LayoutDashboard className="w-4 h-4" /> Dashboard
        </button>
        <div className="w-px h-6 bg-[hsl(var(--border))]" />
        <EcotilePipelineNav counts={stageCounts} active={isStage ? view : null} onSelect={(s) => setView(s)} />
        <div className="ml-auto flex items-center gap-1.5 flex-wrap">
          <button onClick={() => setView("register")} className={secBtn("register")}>Register</button>
          {/* Modules waffle — exact metallic behaviour: WaffleGlyph, rl-frost-btn
              at rest, zinc pressed-in while open, dots warm to brand when a
              module view is active; rl-frost menu with rl-tile items. */}
          {(() => {
            const MODS = [["stores", "Stores", Warehouse, "#3b82f6", "Raw + finished goods stores"], ["payments", "Payments", IndianRupee, "#188653", "Collection board & deadlines"], ["targets", "Targets", Activity, "#a855f7", "Monthly booked vs target"]];
            const onModule = ["stores", "payments", "targets"].includes(view);
            return (
              <div className="relative">
                <button onClick={() => setModOpen((v) => !v)} data-testid="eco-nav-modules"
                  title="Modules — Stores, Payments, Targets" aria-label="Modules"
                  className={`inline-flex items-center justify-center w-[34px] h-[34px] rounded-full shrink-0 transition-[colors,box-shadow] duration-150 ${modOpen ? "bg-zinc-200 dark:bg-zinc-800 text-[hsl(var(--muted-foreground))] [box-shadow:inset_0_1px_3px_rgba(0,0,0,0.16),inset_0_0_0_1px_rgba(0,0,0,0.06)]" : onModule ? "rl-frost-btn text-[#B78E10]" : "rl-frost-btn text-[hsl(var(--muted-foreground))] hover:text-[#B78E10]"}`}>
                  <WaffleGlyph size={16} mono />
                </button>
                {modOpen && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setModOpen(false)} />
                    <div className="absolute right-0 top-full mt-2 z-50 w-72 rounded-xl rl-frost rl-hero-glass border border-[hsl(var(--border))] shadow-2xl p-2">
                      <div className="px-2 pt-1 pb-2 text-[11px] text-[hsl(var(--muted-foreground))]">Modules</div>
                      {MODS.map(([k, label, Ic, accent, desc]) => (
                        <button key={k} onClick={() => { setView(k); setModOpen(false); }} data-testid={`eco-mod-${k}`}
                          className={`w-full flex items-center gap-3 p-2 rounded-lg text-left rl-tile ${view === k ? "rl-pressed" : ""}`}>
                          <span className="rl-ico-well w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ color: accent }}><Ic className="w-[18px] h-[18px]" /></span>
                          <span className="flex-1 min-w-0">
                            <span className="block text-[13px] font-medium text-[hsl(var(--foreground))]">{label}</span>
                            <span className="block text-[11px] text-[hsl(var(--muted-foreground))] truncate">{desc}</span>
                          </span>
                          <ArrowRight className="w-4 h-4 text-[hsl(var(--muted-foreground))] shrink-0" />
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            );
          })()}
          {canManage && (
            <button onClick={() => setFormFor("new")} data-testid="eco-new-order"
              className="h-8 px-3.5 rounded-full text-[12.5px] font-bold shadow-sm hover:brightness-105"
              style={{ background: "#FFCC00", color: "#2b2500" }}>+ New order</button>
          )}
        </div>
      </div>

      {view === "dashboard" && <DashboardView summary={summary} targets={targets} orders={orders} stageCounts={stageCounts} activity={activity} dark={dark} go={setView} goRegister={goRegister} openOrder={openOrder} canManage={canManage} onImport={() => setImportOpen(true)} onExport={() => exportOrders(orders)} />}
      {isStage && <StageView key={view} stage={view} orders={orders} canManage={canManage} canRecord={canRecord} reload={load} onEdit={setFormFor} focus={stageFocus} onFocusDone={() => setStageFocus(null)} />}
      {view === "register" && (
        <Register orders={orders} filter={filter} setFilter={setFilter} q={q} setQ={setQ}
                  openId={openId} setOpenId={setOpenId} canRecord={canRecord} canManage={canManage} reload={load} onEdit={setFormFor} />
      )}
      {view === "stores" && <EcotileStores />}
      {view === "payments" && <PaymentsBoard orders={orders} />}
      {view === "targets" && <TargetsView targets={targets} dark={dark} />}
      {view === "history" && <HistoryView activity={activity} orders={orders} openOrder={openOrder} onBack={() => setView("dashboard")} />}

      {formFor && (
        <OrderForm initial={formFor === "new" ? null : formFor} orgNames={orgNames}
                   onClose={() => setFormFor(null)} onSaved={() => { setFormFor(null); load(); }} />
      )}
      {importOpen && (
        <EcotileImportModal orders={orders} onClose={() => setImportOpen(false)} onImported={load} />
      )}
    </div>
  );
}
